import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { ResponsiveContainer, BarChart as BarChart$1, CartesianGrid, XAxis, YAxis, ReferenceLine, Tooltip, Bar, Cell, LabelList, LineChart as LineChart$1, Line, AreaChart as AreaChart$1, Area, PieChart as PieChart$1, Pie, RadialBarChart, PolarAngleAxis, RadialBar, Sankey } from 'recharts';
import * as React5 from 'react';
import { useState, useEffect } from 'react';
import { Percent, DollarSign, Search, ArrowDown, ArrowUp, CheckIcon, CircleIcon, ChevronRightIcon, ChevronDown, XIcon, ChevronDownIcon, ChevronUpIcon, PanelLeftOpen, PanelLeftClose, Menu, ChevronRight, X, ExternalLink, Info, TriangleAlert, CheckCircle2, AlertCircle, Database, Filter, Layers3 } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { Slot, DropdownMenu as DropdownMenu$1, Dialog as Dialog$1, Collapsible as Collapsible$1, Select as Select$1, Separator as Separator$1, Tabs as Tabs$1, Tooltip as Tooltip$1 } from 'radix-ui';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Toaster as Toaster$1, toast } from 'sonner';
export { toast } from 'sonner';

// src/Money.tsx
var DEFAULT_LOCALE = "en-US";
var DEFAULT_CURRENCY = "USD";
function formatMoney(value, opts = {}) {
  const compact = opts.notation === "compact";
  const formatter = new Intl.NumberFormat(opts.locale ?? DEFAULT_LOCALE, {
    style: "currency",
    currency: opts.currency ?? DEFAULT_CURRENCY,
    notation: opts.notation,
    minimumFractionDigits: opts.minimumFractionDigits ?? (compact ? 0 : 2),
    maximumFractionDigits: opts.maximumFractionDigits ?? (compact ? 1 : 2)
  });
  if (!Number.isFinite(value)) return "";
  return formatter.format(value);
}
function Money({
  value,
  currency,
  locale,
  minimumFractionDigits,
  maximumFractionDigits,
  className,
  title,
  placeholder = "\u2014"
}) {
  if (!Number.isFinite(value)) {
    return /* @__PURE__ */ jsx("span", { className, children: placeholder });
  }
  const text = formatMoney(value, {
    currency,
    locale,
    minimumFractionDigits,
    maximumFractionDigits
  });
  return /* @__PURE__ */ jsx("span", { className, title: title ?? text, children: text });
}
var DEFAULT_LOCALE2 = "en-US";
function formatDateTime(value, opts = {}) {
  if (value == null) return "\u2014";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const locale = opts.locale ?? DEFAULT_LOCALE2;
  const fmt = opts.format ?? "datetime";
  const tz = opts.timeZone ? { timeZone: opts.timeZone } : {};
  if (fmt === "iso") return d.toISOString();
  if (fmt === "relative") {
    return _relative(d, opts.now ?? /* @__PURE__ */ new Date(), locale);
  }
  if (fmt === "date") {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium", ...tz }).format(d);
  }
  if (fmt === "time") {
    return new Intl.DateTimeFormat(locale, { timeStyle: "short", ...tz }).format(d);
  }
  if (fmt === "datetime-short") {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
      ...tz
    }).format(d);
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    ...tz
  }).format(d);
}
function formatTimeBucket(value, grain, locale) {
  if (value == null) return "\u2014";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const loc = locale ?? DEFAULT_LOCALE2;
  const utc = { timeZone: "UTC" };
  if (grain === "year") {
    return new Intl.DateTimeFormat(loc, { ...utc, year: "numeric" }).format(d);
  }
  if (grain === "month") {
    return new Intl.DateTimeFormat(loc, { ...utc, month: "short", year: "numeric" }).format(d);
  }
  const dayLabel = new Intl.DateTimeFormat(
    loc,
    { ...utc, month: "short", day: "numeric", year: "numeric" }
  ).format(d);
  return grain === "week" ? `Wk of ${dayLabel}` : dayLabel;
}
var RELATIVE_BUCKETS = [
  [60, "second"],
  [3600, "minute"],
  [86400, "hour"],
  [86400 * 30, "day"],
  [86400 * 365, "month"],
  [Infinity, "year"]
];
function _relative(d, now, locale) {
  const seconds = Math.round((d.getTime() - now.getTime()) / 1e3);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (abs < 30) return rtf.format(0, "second");
  let prevDivisor = 1;
  for (const [divisorTop, unit] of RELATIVE_BUCKETS) {
    if (abs < divisorTop) {
      const value = Math.round(seconds / prevDivisor);
      return rtf.format(value, unit);
    }
    prevDivisor = divisorTop;
  }
  return rtf.format(Math.round(seconds / (86400 * 365)), "year");
}
function DateTime({
  value,
  format = "datetime",
  locale,
  now,
  className,
  title,
  timeZone
}) {
  const text = formatDateTime(value, { format, locale, now, timeZone });
  let dateTimeAttr;
  if (value != null) {
    const d = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(d.getTime())) dateTimeAttr = d.toISOString();
  }
  return /* @__PURE__ */ jsx("time", { className, dateTime: dateTimeAttr, title: title ?? text, children: text });
}
var DEFAULT_LOCALE3 = "en-US";
function formatNumber(value, opts = {}) {
  if (value == null || Number.isNaN(value)) return "";
  const style = opts.style ?? "decimal";
  const locale = opts.locale ?? DEFAULT_LOCALE3;
  const intlOpts = {};
  if (style === "percent") intlOpts.style = "percent";
  if (style === "compact") intlOpts.notation = "compact";
  if (opts.minimumFractionDigits !== void 0) intlOpts.minimumFractionDigits = opts.minimumFractionDigits;
  if (opts.maximumFractionDigits !== void 0) intlOpts.maximumFractionDigits = opts.maximumFractionDigits;
  return new Intl.NumberFormat(locale, intlOpts).format(value);
}
function NumberDisplay({
  value,
  style = "decimal",
  locale,
  minimumFractionDigits,
  maximumFractionDigits,
  unit,
  placeholder = "\u2014",
  className,
  title
}) {
  if (value == null || Number.isNaN(value)) {
    return /* @__PURE__ */ jsx("span", { className, children: placeholder });
  }
  const text = formatNumber(value, {
    style,
    locale,
    minimumFractionDigits,
    maximumFractionDigits
  });
  const display = unit ? `${text}${unit}` : text;
  return /* @__PURE__ */ jsx("span", { className, title: title ?? display, children: display });
}
var PRIMARY_COLOR = "var(--brand-primary, #0F1737)";
var CATEGORICAL_PALETTE = [
  "#3B82F6",
  // blue
  "#F59E0B",
  // amber
  "#8B5CF6",
  // violet
  "#EC4899",
  // pink
  "#14B8A6",
  // teal
  "#F97316",
  // orange
  "#6366F1",
  // indigo
  "#84CC16",
  // lime
  "#10B981",
  // emerald  (green pushed down — reads as "savings")
  "#EF4444"
  // red      (pushed last — reads as "overspend/alert")
];
var CHART_TYPOGRAPHY = {
  tickFontSize: 11.5,
  legendFontSize: 12,
  tooltipHeaderFontSize: 12.5,
  tooltipRowFontSize: 12
};
var CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 };
var Y_AXIS_WIDTH = 72;
var CHART_ICON_COLORS = {
  "kpi-card": "text-skyxops-lilac",
  pie: "text-skyxops-lilac",
  bar: "text-skyxops-beach",
  "horizontal-bar": "text-skyxops-beach",
  "stacked-bar": "text-skyxops-beach",
  line: "text-skyxops-orange",
  "multi-line": "text-skyxops-orange",
  area: "text-skyxops-orange",
  sankey: "text-skyxops-midnight dark:text-skyxops-mist",
  "data-grid": "text-muted-foreground"
};
function chartIconColor(renderer) {
  return CHART_ICON_COLORS[renderer] ?? "text-muted-foreground";
}
var DEFAULT_USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
function defaultFormatValue(n) {
  return DEFAULT_USD.format(n);
}
var ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|$)/;
function formatCatLabel(value) {
  const s = String(value ?? "");
  if (typeof value !== "string" || !ISO_DATE_RE.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(d);
}
function timeAxisTickFormatter(labels) {
  const iso = labels.filter((l) => typeof l === "string" && ISO_DATE_RE.test(l));
  if (iso.length === 0) return (v) => String(v ?? "");
  const monthly = iso.every((l) => {
    const d = new Date(l);
    return !Number.isNaN(d.getTime()) && d.getUTCDate() === 1;
  });
  const fmt = new Intl.DateTimeFormat(
    "en-US",
    monthly ? { month: "short", year: "numeric", timeZone: "UTC" } : { month: "short", day: "numeric", timeZone: "UTC" }
  );
  return (v) => {
    const s = String(v ?? "");
    if (typeof v === "string" && ISO_DATE_RE.test(s)) {
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? s : fmt.format(d);
    }
    return s;
  };
}
function xAxisLabelMode(_labels, mode, interval) {
  const angleDeg = mode === "angle" ? 45 : mode === "vertical" ? 90 : 0;
  if (interval != null) return { interval, angleDeg };
  if (mode === "all" || mode === "angle" || mode === "vertical") return { interval: 0, angleDeg };
  return { interval: "preserveStartEnd", angleDeg: 0 };
}
function tickCharBudget(gutterPx, fontSizePx = CHART_TYPOGRAPHY.tickFontSize) {
  return Math.max(6, Math.floor((gutterPx - 10) / (fontSizePx * 0.55)));
}
function truncateTickLabel(label, maxChars) {
  return label.length > maxChars ? `${label.slice(0, Math.max(1, maxChars - 1))}\u2026` : label;
}
function paletteFor(index, override) {
  if (override) return override;
  return CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];
}
var SEMANTIC_COLORS = {
  savings: "#10B981",
  // emerald
  overspend: "#EF4444",
  // red
  anomaly: "#EF4444",
  budget: "#F59E0B",
  // amber
  warning: "#F59E0B",
  forecast: "#06b6d4",
  // info cyan
  actual: PRIMARY_COLOR,
  neutral: PRIMARY_COLOR
};
var RESERVED_COLORS = {
  "(unallocated)": "#A78BFA",
  // violet — matches the §4 sentinel bucket
  "(unknown)": "#94A3B8",
  // muted slate
  "(global)": "#94A3B8",
  "(other)": "#94A3B8"
};
function colorFor(key, role) {
  if (role && SEMANTIC_COLORS[role]) return SEMANTIC_COLORS[role];
  if (RESERVED_COLORS[key]) return RESERVED_COLORS[key];
  const norm = key.trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < norm.length; i++) h = Math.imul(h, 31) + norm.charCodeAt(i) >>> 0;
  return CATEGORICAL_PALETTE[h % CATEGORICAL_PALETTE.length];
}
var LIGHT_COLORS = {
  grid: "#E5E7EB",
  tick: "#525E7C",
  // Subtle, semi-transparent column highlight on hover (AI-UI-161) — a solid fill
  // painted OVER the bars and hid them ("bars go transparent"); a low-opacity scrim
  // keeps the bars visible with a professional highlight.
  tooltipCursor: "rgba(30, 41, 59, 0.06)",
  tooltipBg: "#FFFFFF",
  tooltipBorder: "#E5E7EB",
  tooltipText: "#1E293B"
};
var DARK_COLORS = {
  grid: "#1E293B",
  // skyxops-ink
  tick: "#94A3B8",
  // muted mist
  tooltipCursor: "rgba(226, 232, 240, 0.08)",
  // subtle light scrim (AI-UI-161)
  tooltipBg: "#0F172A",
  // skyxops-midnight
  tooltipBorder: "#1E293B",
  tooltipText: "#F1F5F9"
  // skyxops-mist
};
function useChartColors() {
  const [dark, setDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    if (typeof document === "undefined") return;
    const target = document.documentElement;
    const obs = new MutationObserver(() => {
      setDark(target.classList.contains("dark"));
    });
    obs.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark ? DARK_COLORS : LIGHT_COLORS;
}
function ChartTooltip({
  active,
  payload,
  label,
  formatValue,
  hideZero = true,
  maxRows = 12,
  showTotal = false,
  totalLabel = "Total",
  activeKey
}) {
  const c = useChartColors();
  if (!active || !payload || payload.length === 0) return null;
  const fmt = formatValue ?? ((n) => String(n));
  const total = payload.reduce((s, p) => s + Number(p.value ?? 0), 0);
  const rows = (hideZero ? payload.filter((p) => Number(p.value) !== 0) : payload).slice().sort((a, b) => Number(b.value ?? 0) - Number(a.value ?? 0));
  if (rows.length === 0) return null;
  const shown = rows.slice(0, maxRows);
  const hidden = rows.length - shown.length;
  const sublabel = payload[0]?.payload?.sublabel;
  const header = label != null ? sublabel ? `${formatCatLabel(label)} \xB7 ${sublabel}` : formatCatLabel(label) : sublabel;
  const card = {
    background: c.tooltipBg,
    border: `1px solid ${c.tooltipBorder}`,
    color: c.tooltipText,
    borderRadius: 8,
    padding: "10px 12px",
    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.12)",
    fontSize: CHART_TYPOGRAPHY.tooltipRowFontSize,
    minWidth: 176,
    maxWidth: 288
  };
  if (!showTotal && shown.length === 1) {
    const p = shown[0];
    const name = label != null ? header : String(p.name ?? "");
    const valStr = typeof p.value === "number" ? fmt(p.value) : String(p.value ?? "\u2014");
    return /* @__PURE__ */ jsxs("div", { style: { ...card, minWidth: 0, whiteSpace: "nowrap", padding: "7px 11px" }, children: [
      /* @__PURE__ */ jsxs("span", { style: { opacity: 0.85 }, children: [
        name,
        ": "
      ] }),
      /* @__PURE__ */ jsx("span", { style: { fontWeight: 700, fontVariantNumeric: "tabular-nums" }, children: valStr })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { style: card, children: [
    header != null && /* @__PURE__ */ jsx("div", { style: { fontWeight: 600, fontSize: CHART_TYPOGRAPHY.tooltipHeaderFontSize, marginBottom: 8, textAlign: "center" }, children: header }),
    /* @__PURE__ */ jsx("div", { style: { display: "grid", gridTemplateColumns: "auto 1fr auto", columnGap: 14, rowGap: 5, alignItems: "center" }, children: shown.map((p, i) => {
      const on = activeKey != null && String(p.dataKey) === String(activeKey);
      return /* @__PURE__ */ jsxs(React5.Fragment, { children: [
        /* @__PURE__ */ jsx("span", { style: { width: 10, height: 10, borderRadius: 2, background: p.color ?? p.payload?.fill ?? c.tick, display: "inline-block", outline: on ? `2px solid ${c.tooltipText}` : "none", outlineOffset: 1 } }),
        /* @__PURE__ */ jsx("span", { style: { opacity: on ? 1 : 0.85, fontWeight: on ? 700 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: p.name }),
        /* @__PURE__ */ jsx("span", { style: { fontWeight: on ? 800 : 600, fontVariantNumeric: "tabular-nums", textAlign: "right" }, children: typeof p.value === "number" ? fmt(p.value) : String(p.value ?? "\u2014") })
      ] }, p.dataKey ?? i);
    }) }),
    hidden > 0 && /* @__PURE__ */ jsxs("div", { style: { marginTop: 6, opacity: 0.6 }, children: [
      "+",
      hidden,
      " more"
    ] }),
    showTotal && /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 14,
          marginTop: 8,
          paddingTop: 8,
          borderTop: `1px solid ${c.tooltipBorder}`,
          fontWeight: 600
        },
        children: [
          /* @__PURE__ */ jsx("span", { children: totalLabel }),
          /* @__PURE__ */ jsx("span", { style: { fontVariantNumeric: "tabular-nums" }, children: fmt(total) })
        ]
      }
    )
  ] });
}
var SERIES_COLOR = CATEGORICAL_PALETTE[0];
var BAR_GAP = { narrow: "42%", wide: "4%" };
function BarChart({
  data,
  height = 200,
  formatValue,
  className,
  onSelect,
  showValues = false,
  axisTitle,
  showGrid = true,
  yMin,
  yMax,
  targetLine,
  pointColor,
  barWidth,
  xLabels,
  catInterval,
  valueTicks
}) {
  const fmt = formatValue ?? defaultFormatValue;
  const c = useChartColors();
  const _vals = data.map((d) => Number(d.value));
  const _maxAbs = Math.max(0, ..._vals.map((v) => Math.abs(v)));
  const materialNegative = Math.min(0, ..._vals) < -_maxAbs * 0.02;
  const axisFloor = materialNegative ? Math.min(0, ..._vals) : 0;
  const _dataMax = Math.max(0, ..._vals);
  const _autoTop = showValues && _dataMax > 0 ? _dataMax * 1.14 : "auto";
  const fmtLabel = timeAxisTickFormatter(data.map((d) => d.label));
  const { interval: xInterval, angleDeg } = xAxisLabelMode(data.map((d) => d.label), xLabels, catInterval);
  const maxTickChars = angleDeg === 90 ? 13 : 15;
  const truncTick = (s) => s.length > maxTickChars ? `${s.slice(0, maxTickChars - 1)}\u2026` : s;
  const AngledTick = (p) => {
    const full = String(fmtLabel(p.payload?.value ?? ""));
    const shown = truncTick(full);
    return /* @__PURE__ */ jsxs(
      "text",
      {
        x: p.x,
        y: p.y,
        dy: angleDeg === 90 ? 4 : 10,
        textAnchor: "end",
        transform: `rotate(${-angleDeg} ${p.x} ${p.y})`,
        fontSize: CHART_TYPOGRAPHY.tickFontSize,
        fill: c.tick,
        children: [
          shown !== full && /* @__PURE__ */ jsx("title", { children: full }),
          shown
        ]
      }
    );
  };
  return /* @__PURE__ */ jsx("div", { className, style: { width: "100%", height, cursor: onSelect ? "pointer" : void 0 }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
    BarChart$1,
    {
      data,
      margin: CHART_MARGIN,
      barCategoryGap: barWidth ? BAR_GAP[barWidth] : void 0,
      onClick: onSelect ? (s) => {
        if (s?.activeLabel != null) onSelect(String(s.activeLabel));
      } : void 0,
      children: [
        showGrid && /* @__PURE__ */ jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: c.grid, vertical: false }),
        /* @__PURE__ */ jsx(
          XAxis,
          {
            dataKey: "label",
            interval: xInterval,
            height: angleDeg ? angleDeg === 90 ? 96 : 72 : void 0,
            tickFormatter: angleDeg ? void 0 : fmtLabel,
            tick: angleDeg ? /* @__PURE__ */ jsx(AngledTick, {}) : { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: { stroke: c.grid },
            tickLine: false
          }
        ),
        /* @__PURE__ */ jsx(
          YAxis,
          {
            tickFormatter: fmt,
            tick: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: false,
            tickLine: false,
            width: Y_AXIS_WIDTH,
            domain: [yMin ?? axisFloor, yMax ?? _autoTop],
            ...valueTicks ? { tickCount: valueTicks } : {},
            allowDataOverflow: !materialNegative,
            label: axisTitle ? { value: axisTitle, angle: -90, position: "insideLeft", style: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick, textAnchor: "middle" } } : void 0
          }
        ),
        materialNegative && /* @__PURE__ */ jsx(ReferenceLine, { y: 0, stroke: c.tick, strokeWidth: 1 }),
        targetLine != null && /* @__PURE__ */ jsx(ReferenceLine, { y: targetLine, stroke: "#16a34a", strokeWidth: 1.5, strokeDasharray: "5 3", ifOverflow: "extendDomain", label: { value: `Target ${fmt(targetLine)}`, position: "right", fontSize: 10, fill: "#16a34a" } }),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            content: /* @__PURE__ */ jsx(ChartTooltip, { formatValue: fmt }),
            cursor: { fill: c.tooltipCursor },
            allowEscapeViewBox: { x: false, y: true },
            wrapperStyle: { zIndex: 50, pointerEvents: "auto" }
          }
        ),
        /* @__PURE__ */ jsxs(Bar, { dataKey: "value", fill: SERIES_COLOR, radius: [3, 3, 0, 0], isAnimationActive: false, children: [
          pointColor && data.map((d, i) => /* @__PURE__ */ jsx(Cell, { fill: pointColor(d.value) ?? SERIES_COLOR }, `c-${i}`)),
          showValues && /* @__PURE__ */ jsx(LabelList, { dataKey: "value", position: "top", formatter: (v) => fmt(Number(v)), style: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick, fontWeight: 600 } })
        ] })
      ]
    }
  ) }) });
}
function ChartLegend({ series, rows, max = 10, vertical = false, showValues = false, formatValue }) {
  const c = useChartColors();
  if (!series || series.length === 0) return null;
  const total = /* @__PURE__ */ new Map();
  for (const s of series) {
    let t = 0;
    for (const r of rows) t += Number(r.values[s.key] ?? 0);
    total.set(s.key, t);
  }
  const ranked = [...series].sort((a, b) => (total.get(b.key) ?? 0) - (total.get(a.key) ?? 0));
  const shown = ranked.slice(0, max);
  const hidden = ranked.length - shown.length;
  const hiddenTotal = ranked.slice(max).reduce((a, s) => a + (total.get(s.key) ?? 0), 0);
  const withVal = showValues && formatValue ? formatValue : null;
  const dot = (color) => ({
    width: 10,
    height: 10,
    borderRadius: 2,
    background: color,
    display: "inline-block",
    marginRight: 7,
    flexShrink: 0
  });
  const item = { display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" };
  const container = vertical ? { display: "flex", flexDirection: "column", flexWrap: "nowrap", alignItems: "flex-start", gap: 8, fontSize: CHART_TYPOGRAPHY.legendFontSize, lineHeight: 1.35, color: c.tick, maxHeight: "100%", overflowY: "auto" } : { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "7px 18px", paddingTop: 12, fontSize: CHART_TYPOGRAPHY.legendFontSize, lineHeight: 1.35, color: c.tick, maxHeight: 54, overflow: "hidden" };
  return /* @__PURE__ */ jsxs("div", { style: container, children: [
    shown.map((s) => /* @__PURE__ */ jsxs("span", { style: item, children: [
      /* @__PURE__ */ jsx("span", { style: dot(s.color ?? c.tick) }),
      s.name,
      withVal && /* @__PURE__ */ jsxs("span", { style: { marginLeft: 5, opacity: 0.72 }, children: [
        "\xB7 ",
        withVal(total.get(s.key) ?? 0)
      ] })
    ] }, s.key)),
    hidden > 0 && /* @__PURE__ */ jsxs("span", { style: { ...item, opacity: 0.72 }, title: `${hidden} more series aggregated in the chart`, children: [
      /* @__PURE__ */ jsx("span", { style: dot("#94A3B8") }),
      "Others \xB7 ",
      hidden,
      " more",
      withVal && /* @__PURE__ */ jsxs("span", { style: { marginLeft: 5 }, children: [
        "\xB7 ",
        withVal(hiddenTotal)
      ] })
    ] })
  ] });
}
var SERIES_COLOR2 = CATEGORICAL_PALETTE[0];
var BAR_GAP2 = { narrow: "42%", wide: "4%" };
function HorizontalBarChart({
  data,
  height = 240,
  formatValue,
  labelWidth = 140,
  className,
  onSelect,
  showValues = false,
  axisTitle,
  showGrid = true,
  yMin,
  yMax,
  targetLine,
  pointColor,
  barWidth,
  valueTicks,
  xLabels,
  catInterval
}) {
  const fmt = formatValue ?? defaultFormatValue;
  const c = useChartColors();
  const _vals = data.map((d) => Number(d.value));
  const _maxAbs = Math.max(0, ..._vals.map((v) => Math.abs(v)));
  const materialNegative = Math.min(0, ..._vals) < -_maxAbs * 0.02;
  const axisFloor = materialNegative ? Math.min(0, ..._vals) : 0;
  const maxYLabels = Math.max(4, Math.floor(height / 22));
  const yInterval = data.length > maxYLabels ? Math.ceil(data.length / maxYLabels) - 1 : 0;
  const yLabelInterval = catInterval != null ? catInterval : xLabels && xLabels !== "auto" ? 0 : yInterval;
  const yTickLabel = timeAxisTickFormatter(data.map((d) => d.label));
  const maxTickChars = tickCharBudget(labelWidth);
  const CategoryTick = (p) => {
    const full = String(yTickLabel(p.payload?.value ?? ""));
    const shown = truncateTickLabel(full, maxTickChars);
    return /* @__PURE__ */ jsx(
      "text",
      {
        x: p.x,
        y: p.y,
        dy: "0.32em",
        textAnchor: "end",
        fontSize: CHART_TYPOGRAPHY.tickFontSize,
        fill: c.tooltipText,
        children: shown
      }
    );
  };
  const renderValueLabel = (p) => {
    if (p.x == null || p.width == null) return null;
    const tx = Number(p.x) + Number(p.width) + 5;
    const ty = Number(p.y ?? 0) + Number(p.height ?? 0) / 2;
    return /* @__PURE__ */ jsx("text", { x: tx, y: ty, dy: "0.32em", textAnchor: "start", fontSize: CHART_TYPOGRAPHY.tickFontSize, fontWeight: 600, fill: c.tick, children: fmt(Number(p.value ?? 0)) });
  };
  return /* @__PURE__ */ jsx("div", { className, style: { width: "100%", height, cursor: onSelect ? "pointer" : void 0 }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
    BarChart$1,
    {
      data,
      layout: "vertical",
      barCategoryGap: barWidth ? BAR_GAP2[barWidth] : void 0,
      margin: { top: 12, right: 16, bottom: axisTitle ? 24 : 8, left: 0 },
      onClick: onSelect ? (s) => {
        if (s?.activeLabel != null) onSelect(String(s.activeLabel));
      } : void 0,
      children: [
        showGrid && /* @__PURE__ */ jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: c.grid, horizontal: false }),
        /* @__PURE__ */ jsx(
          XAxis,
          {
            type: "number",
            tickFormatter: fmt,
            tick: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: false,
            tickLine: false,
            domain: [yMin ?? axisFloor, yMax ?? "auto"],
            ...valueTicks ? { tickCount: valueTicks } : {},
            allowDataOverflow: !materialNegative,
            label: axisTitle ? { value: axisTitle, position: "insideBottom", offset: -4, style: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick, textAnchor: "middle" } } : void 0
          }
        ),
        materialNegative && /* @__PURE__ */ jsx(ReferenceLine, { x: 0, stroke: c.tick, strokeWidth: 1 }),
        targetLine != null && /* @__PURE__ */ jsx(ReferenceLine, { x: targetLine, stroke: "#16a34a", strokeWidth: 1.5, strokeDasharray: "5 3", ifOverflow: "extendDomain", label: { value: `Target ${fmt(targetLine)}`, position: "top", fontSize: 10, fill: "#16a34a" } }),
        /* @__PURE__ */ jsx(
          YAxis,
          {
            type: "category",
            dataKey: "label",
            interval: yLabelInterval,
            tick: CategoryTick,
            axisLine: { stroke: c.grid },
            tickLine: false,
            width: labelWidth
          }
        ),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            content: /* @__PURE__ */ jsx(ChartTooltip, { formatValue: fmt }),
            cursor: { fill: c.tooltipCursor },
            allowEscapeViewBox: { x: false, y: true },
            wrapperStyle: { zIndex: 50, pointerEvents: "auto" }
          }
        ),
        /* @__PURE__ */ jsxs(Bar, { dataKey: "value", fill: SERIES_COLOR2, radius: [0, 3, 3, 0], isAnimationActive: false, children: [
          pointColor && data.map((d, i) => /* @__PURE__ */ jsx(Cell, { fill: pointColor(d.value) ?? SERIES_COLOR2 }, `c-${i}`)),
          showValues && /* @__PURE__ */ jsx(LabelList, { dataKey: "value", content: renderValueLabel })
        ] })
      ]
    }
  ) }) });
}
var SERIES_COLOR3 = CATEGORICAL_PALETTE[0];
function LineChart({
  data,
  height = 200,
  formatValue,
  showDots = true,
  smooth = false,
  className,
  onSelect,
  showValues = false,
  axisTitle,
  showGrid = true,
  yMin,
  yMax,
  targetLine,
  valueTicks
}) {
  const fmt = formatValue ?? defaultFormatValue;
  const c = useChartColors();
  return /* @__PURE__ */ jsx("div", { className, style: { width: "100%", height, cursor: onSelect ? "pointer" : void 0 }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
    LineChart$1,
    {
      data,
      margin: CHART_MARGIN,
      onClick: onSelect ? (s) => {
        if (s?.activeLabel != null) onSelect(String(s.activeLabel));
      } : void 0,
      children: [
        showGrid && /* @__PURE__ */ jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: c.grid, vertical: false }),
        /* @__PURE__ */ jsx(
          XAxis,
          {
            dataKey: "label",
            tickFormatter: timeAxisTickFormatter(data.map((d) => d.label)),
            tick: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: { stroke: c.grid },
            tickLine: false
          }
        ),
        /* @__PURE__ */ jsx(
          YAxis,
          {
            tickFormatter: fmt,
            tick: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: false,
            tickLine: false,
            width: Y_AXIS_WIDTH,
            domain: [yMin ?? "auto", yMax ?? "auto"],
            ...valueTicks ? { tickCount: valueTicks } : {},
            label: axisTitle ? { value: axisTitle, angle: -90, position: "insideLeft", style: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick, textAnchor: "middle" } } : void 0
          }
        ),
        data.some((d) => Number(d.value) < 0) && /* @__PURE__ */ jsx(ReferenceLine, { y: 0, stroke: c.tick, strokeWidth: 1 }),
        targetLine != null && /* @__PURE__ */ jsx(ReferenceLine, { y: targetLine, stroke: "#16a34a", strokeWidth: 1.5, strokeDasharray: "5 3", ifOverflow: "extendDomain", label: { value: `Target ${fmt(targetLine)}`, position: "right", fontSize: 10, fill: "#16a34a" } }),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            content: /* @__PURE__ */ jsx(ChartTooltip, { formatValue: fmt }),
            cursor: { stroke: c.grid },
            allowEscapeViewBox: { x: false, y: true },
            wrapperStyle: { zIndex: 50, pointerEvents: "auto" }
          }
        ),
        /* @__PURE__ */ jsx(
          Line,
          {
            type: smooth ? "monotone" : "linear",
            dataKey: "value",
            stroke: SERIES_COLOR3,
            strokeWidth: 2,
            dot: showDots ? { r: 3, fill: SERIES_COLOR3 } : false,
            activeDot: { r: 5 },
            isAnimationActive: false,
            children: showValues && /* @__PURE__ */ jsx(LabelList, { dataKey: "value", position: "top", formatter: (v) => fmt(Number(v)), style: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick, fontWeight: 600 } })
          }
        )
      ]
    }
  ) }) });
}
var SERIES_COLOR4 = CATEGORICAL_PALETTE[0];
var GRADIENT_ID = "skyxops-area-gradient";
function AreaChart({
  data,
  height = 200,
  formatValue,
  smooth = true,
  className,
  onSelect,
  showValues = false,
  axisTitle,
  showGrid = true,
  yMin,
  yMax,
  targetLine,
  valueTicks
}) {
  const fmt = formatValue ?? defaultFormatValue;
  const c = useChartColors();
  return /* @__PURE__ */ jsx("div", { className, style: { width: "100%", height, cursor: onSelect ? "pointer" : void 0 }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
    AreaChart$1,
    {
      data,
      margin: CHART_MARGIN,
      onClick: onSelect ? (s) => {
        if (s?.activeLabel != null) onSelect(String(s.activeLabel));
      } : void 0,
      children: [
        /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsxs("linearGradient", { id: GRADIENT_ID, x1: "0", y1: "0", x2: "0", y2: "1", children: [
          /* @__PURE__ */ jsx("stop", { offset: "5%", stopColor: SERIES_COLOR4, stopOpacity: 0.4 }),
          /* @__PURE__ */ jsx("stop", { offset: "95%", stopColor: SERIES_COLOR4, stopOpacity: 0 })
        ] }) }),
        showGrid && /* @__PURE__ */ jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: c.grid, vertical: false }),
        /* @__PURE__ */ jsx(
          XAxis,
          {
            dataKey: "label",
            tickFormatter: timeAxisTickFormatter(data.map((d) => d.label)),
            tick: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: { stroke: c.grid },
            tickLine: false
          }
        ),
        /* @__PURE__ */ jsx(
          YAxis,
          {
            tickFormatter: fmt,
            tick: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: false,
            tickLine: false,
            width: Y_AXIS_WIDTH,
            domain: [yMin ?? "auto", yMax ?? "auto"],
            ...valueTicks ? { tickCount: valueTicks } : {},
            label: axisTitle ? { value: axisTitle, angle: -90, position: "insideLeft", style: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick, textAnchor: "middle" } } : void 0
          }
        ),
        data.some((d) => Number(d.value) < 0) && /* @__PURE__ */ jsx(ReferenceLine, { y: 0, stroke: c.tick, strokeWidth: 1 }),
        targetLine != null && /* @__PURE__ */ jsx(ReferenceLine, { y: targetLine, stroke: "#16a34a", strokeWidth: 1.5, strokeDasharray: "5 3", ifOverflow: "extendDomain", label: { value: `Target ${fmt(targetLine)}`, position: "right", fontSize: 10, fill: "#16a34a" } }),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            content: /* @__PURE__ */ jsx(ChartTooltip, { formatValue: fmt }),
            cursor: { stroke: c.grid },
            allowEscapeViewBox: { x: false, y: true },
            wrapperStyle: { zIndex: 50, pointerEvents: "auto" }
          }
        ),
        /* @__PURE__ */ jsx(
          Area,
          {
            type: smooth ? "monotone" : "linear",
            dataKey: "value",
            stroke: SERIES_COLOR4,
            strokeWidth: 2,
            fill: `url(#${GRADIENT_ID})`,
            isAnimationActive: false,
            children: showValues && /* @__PURE__ */ jsx(LabelList, { dataKey: "value", position: "top", formatter: (v) => fmt(Number(v)), style: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick, fontWeight: 600 } })
          }
        )
      ]
    }
  ) }) });
}
function PieChart({
  data,
  height = 240,
  formatValue,
  donut = false,
  showLegend = true,
  showValues = false,
  labelMode = "percent",
  legendValues = false,
  legendPos = "bottom",
  className,
  onSelect
}) {
  const rightLegend = legendPos === "right";
  const fmt = formatValue ?? defaultFormatValue;
  const c = useChartColors();
  const values = data.map((d) => Number(d.value)).filter((v) => Number.isFinite(v));
  const negatives = values.filter((v) => v < 0).length;
  const drawable = values.some((v) => v > 0) && negatives === 0;
  const colors = data.map((d, i) => d.color ?? paletteFor(i));
  const legendSeries = data.map((d, i) => ({ key: d.label, name: d.label, color: colors[i] }));
  const legendRows = [{ values: Object.fromEntries(data.map((d) => [d.label, d.value])) }];
  const legendShowValues = legendValues || showValues;
  if (!drawable) {
    const shape = donut ? "donut" : "pie";
    return /* @__PURE__ */ jsx(
      "div",
      {
        className,
        style: { width: "100%", height, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
        role: "note",
        children: /* @__PURE__ */ jsxs("div", { style: { maxWidth: 380, textAlign: "center", fontSize: CHART_TYPOGRAPHY.legendFontSize, color: c.tick }, children: [
          /* @__PURE__ */ jsxs("div", { style: { fontWeight: 600, color: c.tooltipText }, children: [
            "This data can\u2019t be shown as a ",
            shape
          ] }),
          /* @__PURE__ */ jsx("div", { style: { marginTop: 4 }, children: negatives > 0 ? `${negatives === 1 ? "One value is" : `${negatives} values are`} negative, so the slices don't add up to a whole. Use Column, Bar or Table to see it.` : "There's nothing positive to divide into slices. Use Column, Bar or Table to see it." })
        ] })
      }
    );
  }
  return (
    // Legend below by default; 'right' places it beside the pie (AI-ORBIT-314).
    /* @__PURE__ */ jsxs("div", { className, style: { width: "100%", height, display: "flex", flexDirection: rightLegend ? "row" : "column", alignItems: rightLegend ? "center" : void 0, cursor: onSelect ? "pointer" : void 0 }, children: [
      /* @__PURE__ */ jsx("div", { style: { flex: 1, minHeight: 0, minWidth: 0, width: rightLegend ? void 0 : "100%", height: rightLegend ? "100%" : void 0 }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(PieChart$1, { children: [
        /* @__PURE__ */ jsx(
          Pie,
          {
            data,
            dataKey: "value",
            nameKey: "label",
            innerRadius: donut ? "55%" : 0,
            outerRadius: "80%",
            paddingAngle: 0,
            isAnimationActive: false,
            label: showValues ? (e) => {
              if ((e.percent ?? 0) < 0.03) return "";
              return labelMode === "value" ? fmt(Number(e.value ?? 0)) : `${Math.round((e.percent ?? 0) * 100)}%`;
            } : false,
            labelLine: showValues ? { stroke: "hsl(215 16% 70%)" } : false,
            onClick: onSelect ? (d) => {
              const lbl = d?.payload?.label ?? d?.label ?? d?.name;
              if (lbl != null) onSelect(String(lbl));
            } : void 0,
            children: data.map((d, i) => /* @__PURE__ */ jsx(Cell, { fill: colors[i] }, d.label))
          }
        ),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            content: /* @__PURE__ */ jsx(ChartTooltip, { formatValue: fmt }),
            allowEscapeViewBox: { x: true, y: true },
            wrapperStyle: { zIndex: 50, pointerEvents: "auto" }
          }
        )
      ] }) }) }),
      showLegend && /* @__PURE__ */ jsx("div", { style: rightLegend ? { flex: "0 0 auto", maxWidth: "42%", maxHeight: "100%", paddingLeft: 8, overflow: "hidden" } : void 0, children: /* @__PURE__ */ jsx(ChartLegend, { series: legendSeries, rows: legendRows, vertical: rightLegend, showValues: legendShowValues, formatValue: fmt }) })
    ] })
  );
}
function JobStatusBadge({
  info,
  onDismiss,
  successLabel = "Export ready",
  pendingLabel = "Starting\u2026"
}) {
  if (info == null) {
    return /* @__PURE__ */ jsx("div", { className: "bg-skyxops-mist/40 dark:bg-skyxops-midnight/40 border border-skyxops-mist dark:border-skyxops-midnight rounded p-3 text-sm text-skyxops-ink/70 dark:text-skyxops-mist/70", children: pendingLabel });
  }
  if (info.status === "pending" || info.status === "running") {
    return /* @__PURE__ */ jsxs("div", { className: "bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 text-sm flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("span", { children: [
        "Job ",
        info.status,
        " (attempt ",
        info.attempts,
        "/",
        info.max_attempts,
        ")\u2026"
      ] }),
      /* @__PURE__ */ jsx("span", { className: "ml-3 text-xs text-amber-700/70", children: "checking every 3s" })
    ] });
  }
  if (info.status === "succeeded") {
    const sub = [];
    if (info.row_count !== void 0) sub.push(`${info.row_count.toLocaleString()} rows`);
    if (info.size_bytes !== void 0) sub.push(`${Math.round(info.size_bytes / 1024).toLocaleString()} KB`);
    return /* @__PURE__ */ jsxs("div", { className: "bg-emerald-50 border border-emerald-200 text-emerald-900 rounded p-3 text-sm flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("span", { className: "font-medium", children: successLabel }),
        sub.length > 0 && /* @__PURE__ */ jsx("span", { className: "ml-2 text-xs text-emerald-700/80", children: sub.join(" \xB7 ") })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        info.download_url ? /* @__PURE__ */ jsxs(
          "a",
          {
            href: info.download_url,
            download: info.filename,
            className: "px-3 py-1 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700",
            children: [
              "Download ",
              info.filename ?? "file"
            ]
          }
        ) : /* @__PURE__ */ jsx("span", { className: "text-xs text-emerald-700/80", children: "Ready (no presigned URL \u2014 server-side download not configured)." }),
        /* @__PURE__ */ jsx("button", { onClick: onDismiss, className: "text-xs underline", children: "Dismiss" })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: "bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm flex items-center justify-between", children: [
    /* @__PURE__ */ jsxs("span", { children: [
      "Job ",
      info.status,
      info.error_message ? `: ${info.error_message}` : ""
    ] }),
    /* @__PURE__ */ jsx("button", { onClick: onDismiss, className: "ml-3 underline", children: "Dismiss" })
  ] });
}
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary: "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive: "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline: "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        // Soft semantic status tints (status pills): light tint + saturated text,
        // readable in both themes. Colors live HERE so apps never pick raw hues.
        success: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
        warning: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
        error: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
        info: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot.Root : "span";
  return /* @__PURE__ */ jsx(
    Comp,
    {
      "data-slot": "badge",
      "data-variant": variant,
      className: cn(badgeVariants({ variant }), className),
      ...props
    }
  );
}
function Card({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card",
      className: cn(
        "flex flex-col gap-6 rounded-xl border bg-card py-6 text-card-foreground shadow-sm",
        className
      ),
      ...props
    }
  );
}
function CardHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-header",
      className: cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      ),
      ...props
    }
  );
}
function CardTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-title",
      className: cn("leading-none font-semibold", className),
      ...props
    }
  );
}
function CardDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function CardAction({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-action",
      className: cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      ),
      ...props
    }
  );
}
function CardContent({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-content",
      className: cn("px-6", className),
      ...props
    }
  );
}
function CardFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-footer",
      className: cn("flex items-center px-6 [.border-t]:pt-6", className),
      ...props
    }
  );
}
function Spinner({ className, label = "Loading" }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary", className),
      role: "status",
      "aria-label": label
    }
  );
}

// src/cardStyles.ts
var ELEVATED_CARD_CLASSNAME = "transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-foreground/15";
var KPI_ACCENTS = CATEGORICAL_PALETTE.slice(0, 6);
function kpiIconFor(fmt) {
  switch (fmt) {
    case "currency":
    case "price":
      return /* @__PURE__ */ jsx(DollarSign, { className: "h-4 w-4", "aria-hidden": true });
    case "percent":
      return /* @__PURE__ */ jsx(Percent, { className: "h-4 w-4", "aria-hidden": true });
    default:
      return null;
  }
}
function deltaTone(sign, direction) {
  if (sign === 0) {
    return "secondary";
  }
  const positive = sign > 0;
  const isGood = direction === "up" ? positive : !positive;
  return isGood ? "success" : "error";
}
function KpiCard({
  label,
  value,
  loading = false,
  sub,
  delta,
  deltaSign,
  deltaIsGood = "up",
  interactive = false,
  valueTitle,
  bare = false,
  className,
  icon,
  accentColor
}) {
  const valueNode = loading ? /* @__PURE__ */ jsx(Spinner, { className: "h-6 w-6" }) : value;
  const labelNode = label != null && label !== "" && /* @__PURE__ */ jsx("div", { className: "text-xs font-medium uppercase tracking-wider text-muted-foreground", children: label });
  const inner = /* @__PURE__ */ jsxs(Fragment, { children: [
    icon ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
      /* @__PURE__ */ jsx(
        "span",
        {
          className: cn("shrink-0", accentColor == null && "text-muted-foreground"),
          style: accentColor != null ? { color: accentColor } : void 0,
          children: icon
        }
      ),
      labelNode
    ] }) : labelNode,
    /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "font-display font-semibold tracking-tight tabular-nums text-card-foreground truncate",
          bare ? "text-3xl" : "text-2xl mt-1"
        ),
        title: valueTitle,
        children: valueNode
      }
    ),
    !loading && (sub || delta !== void 0) && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mt-1", children: [
      delta !== void 0 && /* @__PURE__ */ jsx(Badge, { variant: deltaTone(deltaSign ?? 0, deltaIsGood), children: delta }),
      !loading && sub && /* @__PURE__ */ jsx("div", { className: "text-xs text-muted-foreground", children: sub })
    ] })
  ] });
  if (bare) {
    return /* @__PURE__ */ jsxs("div", { className: cn("flex h-full flex-col justify-center gap-1", className), children: [
      label != null && label !== "" && /* @__PURE__ */ jsx("div", { className: "text-xs font-medium uppercase tracking-wider text-muted-foreground", children: label }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-baseline gap-x-2 gap-y-1", children: [
        /* @__PURE__ */ jsx("span", { className: "font-display text-3xl font-semibold tracking-tight tabular-nums text-card-foreground truncate", title: valueTitle, children: valueNode }),
        !loading && delta !== void 0 && /* @__PURE__ */ jsx(Badge, { variant: deltaTone(deltaSign ?? 0, deltaIsGood), children: delta })
      ] }),
      !loading && sub && /* @__PURE__ */ jsx("div", { className: "text-xs text-muted-foreground", children: sub })
    ] });
  }
  return /* @__PURE__ */ jsxs(
    Card,
    {
      className: cn(
        "p-5 gap-2",
        interactive && ELEVATED_CARD_CLASSNAME,
        accentColor != null && "relative overflow-hidden",
        className
      ),
      children: [
        accentColor != null && /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "absolute inset-y-0 left-0 w-1", style: { background: accentColor } }),
        inner
      ]
    }
  );
}
function Pagination({
  page,
  totalPages,
  onPageChange,
  summary,
  prevLabel = "\u2190 Prev",
  nextLabel = "Next \u2192",
  className
}) {
  if (totalPages <= 0) return null;
  const wrapperCls = `flex items-center justify-between px-4 py-3 border-t border-skyxops-mist dark:border-skyxops-midnight text-sm text-skyxops-ink/70 dark:text-skyxops-mist/70 ${className ?? ""}`.trim();
  const buttonCls = "px-3 py-1 rounded border border-skyxops-mist dark:border-skyxops-midnight hover:bg-skyxops-mist/40 dark:hover:bg-skyxops-midnight/50 disabled:opacity-50 disabled:cursor-not-allowed";
  return /* @__PURE__ */ jsxs("div", { className: wrapperCls, children: [
    /* @__PURE__ */ jsx("div", { children: summary }),
    /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => onPageChange(Math.max(1, page - 1)),
          disabled: page <= 1,
          className: buttonCls,
          children: prevLabel
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => onPageChange(Math.min(totalPages, page + 1)),
          disabled: page >= totalPages,
          className: buttonCls,
          children: nextLabel
        }
      )
    ] })
  ] });
}
function StackedBarChart({
  data,
  series,
  height = 240,
  formatValue,
  showLegend = true,
  grouped = false,
  xLabels,
  catInterval,
  valueTicks,
  className
}) {
  const fmt = formatValue ?? defaultFormatValue;
  const c = useChartColors();
  const [activeKey, setActiveKey] = React5.useState(null);
  const rows = React5.useMemo(
    () => data.map((row) => ({ label: row.label, ...row.values })),
    [data]
  );
  const coloredSeries = React5.useMemo(
    () => series.map((s, i) => {
      const isOther = s.name === "Others" || s.name === "(other)" || s.key === "others" || s.key === "__other__";
      return { ...s, color: s.color ?? (isOther ? "#94A3B8" : paletteFor(i)) };
    }),
    [series]
  );
  const hasNegative = React5.useMemo(
    () => data.some((r) => Object.values(r.values).some((v) => Number(v) < 0)),
    [data]
  );
  const fmtLabel = timeAxisTickFormatter(data.map((r) => r.label));
  const { interval: xInterval, angleDeg } = xAxisLabelMode(data.map((r) => r.label), xLabels, catInterval);
  const AngledTick = (p) => /* @__PURE__ */ jsx("text", { x: p.x, y: p.y, dy: angleDeg === 90 ? 4 : 10, textAnchor: "end", transform: `rotate(${-angleDeg} ${p.x} ${p.y})`, fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick, children: fmtLabel(p.payload?.value ?? "") });
  return (
    // Flex column so the CHART (every series renders) gets its full height and the compact
    // legend sits below it — an in-chart recharts <Legend> would eat the plot with many series
    // (AI-UI-161). ChartLegend caps the *legend* to top-N + "(other) · N more", data untouched.
    /* @__PURE__ */ jsxs("div", { className, style: { width: "100%", height, display: "flex", flexDirection: "column" }, children: [
      /* @__PURE__ */ jsx("div", { style: { flex: 1, minHeight: 0, width: "100%" }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(BarChart$1, { data: rows, margin: CHART_MARGIN, children: [
        /* @__PURE__ */ jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: c.grid, vertical: false }),
        /* @__PURE__ */ jsx(
          XAxis,
          {
            dataKey: "label",
            interval: xInterval,
            height: angleDeg ? angleDeg === 90 ? 96 : 72 : void 0,
            tickFormatter: angleDeg ? void 0 : fmtLabel,
            tick: angleDeg ? /* @__PURE__ */ jsx(AngledTick, {}) : { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: { stroke: c.grid },
            tickLine: false
          }
        ),
        /* @__PURE__ */ jsx(
          YAxis,
          {
            tickFormatter: fmt,
            tick: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: false,
            tickLine: false,
            width: Y_AXIS_WIDTH,
            domain: hasNegative ? ["auto", "auto"] : [0, "auto"],
            ...valueTicks ? { tickCount: valueTicks } : {}
          }
        ),
        hasNegative && /* @__PURE__ */ jsx(ReferenceLine, { y: 0, stroke: c.tick, strokeWidth: 1 }),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            content: /* @__PURE__ */ jsx(ChartTooltip, { formatValue: fmt, showTotal: true, activeKey: activeKey ?? void 0 }),
            cursor: { fill: c.tooltipCursor },
            allowEscapeViewBox: { x: false, y: true },
            wrapperStyle: { zIndex: 50, pointerEvents: "auto" }
          }
        ),
        coloredSeries.map((s, i) => /* @__PURE__ */ jsx(
          Bar,
          {
            dataKey: s.key,
            name: s.name,
            stackId: grouped ? void 0 : "stack",
            fill: s.color,
            radius: grouped || i === series.length - 1 ? [3, 3, 0, 0] : 0,
            isAnimationActive: false,
            onMouseEnter: () => setActiveKey(s.key),
            onMouseLeave: () => setActiveKey(null)
          },
          s.key
        ))
      ] }) }) }),
      showLegend && /* @__PURE__ */ jsx(ChartLegend, { series: coloredSeries, rows: data, max: series.length })
    ] })
  );
}
var COLOR_GREEN = "#10B981";
var COLOR_AMBER = "#F59E0B";
var COLOR_RED = "#EF4444";
function autoColor(pct, tone) {
  const ratio = tone === "savings" ? 1 - pct : pct;
  if (ratio < 0.5) return COLOR_GREEN;
  if (ratio < 0.8) return COLOR_AMBER;
  return COLOR_RED;
}
function GaugeChart({
  value,
  max,
  height = 200,
  color,
  tone = "cost",
  centerLabel,
  subLabel,
  className
}) {
  const c = useChartColors();
  const safeMax = max > 0 ? max : 1;
  const pct = Math.max(0, Math.min(1, value / safeMax));
  const fill = color ?? autoColor(pct, tone);
  const data = [{ name: "metric", value }];
  return /* @__PURE__ */ jsxs("div", { className, style: { width: "100%", height, position: "relative" }, children: [
    /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
      RadialBarChart,
      {
        data,
        startAngle: 180,
        endAngle: 0,
        innerRadius: "65%",
        outerRadius: "100%",
        cx: "50%",
        cy: "80%",
        barSize: 18,
        children: [
          /* @__PURE__ */ jsx(PolarAngleAxis, { type: "number", domain: [0, safeMax], tick: false }),
          /* @__PURE__ */ jsx(
            RadialBar,
            {
              background: { fill: c.grid },
              dataKey: "value",
              cornerRadius: 9,
              fill,
              isAnimationActive: false
            }
          )
        ]
      }
    ) }),
    (centerLabel || subLabel) && /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          paddingBottom: "15%",
          pointerEvents: "none"
        },
        children: [
          centerLabel && /* @__PURE__ */ jsx("div", { className: "text-2xl font-bold text-skyxops-midnight dark:text-skyxops-mist", children: centerLabel }),
          subLabel && /* @__PURE__ */ jsx("div", { className: "text-xs text-skyxops-ink/60 dark:text-skyxops-mist/60 mt-1", children: subLabel })
        ]
      }
    )
  ] });
}
function deltaCls(sign, direction) {
  if (sign === 0) {
    return "bg-skyxops-mist/60 text-skyxops-ink/70 dark:bg-skyxops-midnight dark:text-skyxops-mist/70";
  }
  const positive = sign > 0;
  const isGood = direction === "up" ? positive : !positive;
  return isGood ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
}
function TrendIndicator({
  data,
  height = 32,
  width,
  delta,
  deltaSign,
  deltaIsGood = "up",
  color = PRIMARY_COLOR,
  className
}) {
  const series = React5.useMemo(
    () => (data ?? []).map((v, i) => ({ i, v })),
    [data]
  );
  const wrapperCls = `inline-flex items-center gap-2 ${className ?? ""}`.trim();
  return /* @__PURE__ */ jsxs("div", { className: wrapperCls, children: [
    data && data.length > 0 && /* @__PURE__ */ jsx("div", { style: { width: width ?? 80, height }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsx(LineChart$1, { data: series, margin: { top: 2, right: 2, bottom: 2, left: 2 }, children: /* @__PURE__ */ jsx(
      Line,
      {
        type: "monotone",
        dataKey: "v",
        stroke: color,
        strokeWidth: 1.5,
        dot: false,
        isAnimationActive: false
      }
    ) }) }) }),
    delta !== void 0 && /* @__PURE__ */ jsx(
      "span",
      {
        className: `inline-block px-2 py-0.5 rounded text-xs font-medium ${deltaCls(deltaSign ?? 0, deltaIsGood)}`,
        children: delta
      }
    )
  ] });
}
function MultiSeriesLineChart({
  data,
  series,
  height = 240,
  formatValue,
  smooth = false,
  showDots = false,
  showLegend = true,
  xLabels,
  catInterval,
  valueTicks,
  className
}) {
  const fmt = formatValue ?? defaultFormatValue;
  const c = useChartColors();
  const rows = React5.useMemo(
    () => data.map((row) => ({ label: row.label, ...row.values })),
    [data]
  );
  const fmtLabel = timeAxisTickFormatter(data.map((r) => r.label));
  const { interval: xInterval, angleDeg } = xAxisLabelMode(data.map((r) => r.label), xLabels, catInterval);
  const AngledTick = (p) => /* @__PURE__ */ jsx("text", { x: p.x, y: p.y, dy: angleDeg === 90 ? 4 : 10, textAnchor: "end", transform: `rotate(${-angleDeg} ${p.x} ${p.y})`, fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick, children: fmtLabel(p.payload?.value ?? "") });
  return (
    // Flex column: the chart (every series) fills, the compact ChartLegend sits below
    // (top-N + "(other) · N more") so many series never eat the plot height (AI-UI-161).
    /* @__PURE__ */ jsxs("div", { className, style: { width: "100%", height, display: "flex", flexDirection: "column" }, children: [
      /* @__PURE__ */ jsx("div", { style: { flex: 1, minHeight: 0, width: "100%" }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(LineChart$1, { data: rows, margin: CHART_MARGIN, children: [
        /* @__PURE__ */ jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: c.grid, vertical: false }),
        /* @__PURE__ */ jsx(
          XAxis,
          {
            dataKey: "label",
            interval: xInterval,
            height: angleDeg ? angleDeg === 90 ? 96 : 72 : void 0,
            tickFormatter: angleDeg ? void 0 : fmtLabel,
            tick: angleDeg ? /* @__PURE__ */ jsx(AngledTick, {}) : { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: { stroke: c.grid },
            tickLine: false
          }
        ),
        /* @__PURE__ */ jsx(
          YAxis,
          {
            tickFormatter: fmt,
            tick: { fontSize: CHART_TYPOGRAPHY.tickFontSize, fill: c.tick },
            axisLine: false,
            tickLine: false,
            width: Y_AXIS_WIDTH,
            ...valueTicks ? { tickCount: valueTicks } : {}
          }
        ),
        data.some((r) => Object.values(r.values).some((v) => Number(v) < 0)) && /* @__PURE__ */ jsx(ReferenceLine, { y: 0, stroke: c.tick, strokeWidth: 1 }),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            content: /* @__PURE__ */ jsx(ChartTooltip, { formatValue: fmt }),
            cursor: { stroke: c.grid },
            allowEscapeViewBox: { x: false, y: true },
            wrapperStyle: { zIndex: 50, pointerEvents: "auto" }
          }
        ),
        series.map((s, i) => /* @__PURE__ */ jsx(
          Line,
          {
            type: smooth ? "monotone" : "linear",
            dataKey: s.key,
            name: s.name,
            stroke: paletteFor(i, s.color),
            strokeWidth: 2,
            dot: showDots ? { r: 3 } : false,
            activeDot: { r: 5 },
            isAnimationActive: false
          },
          s.key
        ))
      ] }) }) }),
      showLegend && /* @__PURE__ */ jsx(
        ChartLegend,
        {
          series: series.map((s, i) => ({ ...s, color: paletteFor(i, s.color) })),
          rows: data,
          max: series.length
        }
      )
    ] })
  );
}

// src/sankeyHelpers.ts
var GEO = {
  nodeCornerRadius: 3,
  linkOpacity: 0.55,
  linkOpacityHover: 0.9,
  linkOpacityDimmed: 0.15,
  transition: "opacity 0.1s ease-out"
};
function ribbonPath(l) {
  const x0 = l.source.x1, x1 = l.target.x0, xMid = (x0 + x1) / 2, w = l.width / 2;
  const sy0 = l.y0 - w, sy1 = l.y0 + w, ty0 = l.y1 - w, ty1 = l.y1 + w;
  return `M${x0},${sy0} C${xMid},${sy0} ${xMid},${ty0} ${x1},${ty0} L${x1},${ty1} C${xMid},${ty1} ${xMid},${sy1} ${x0},${sy1} Z`;
}
function computeConnected(graph, hovered) {
  const links = /* @__PURE__ */ new Set(), nodes = /* @__PURE__ */ new Set();
  if (!hovered) return { links, nodes };
  const [kind, key] = hovered.split(/:(.+)/);
  if (kind === "link") {
    const i = Number(key);
    const l = graph.links[i];
    if (l) {
      links.add(i);
      nodes.add(l.source.id);
      nodes.add(l.target.id);
    }
  } else {
    nodes.add(key);
    graph.links.forEach((l, i) => {
      if (l.source.id === key || l.target.id === key) {
        links.add(i);
        nodes.add(l.source.id);
        nodes.add(l.target.id);
      }
    });
  }
  return { links, nodes };
}
function truncate(label, max = 16) {
  return label.length > max ? `${label.slice(0, max - 1)}\u2026` : label;
}
function SankeyChart({
  nodes,
  links,
  height = 320,
  formatValue,
  nodeWidth = 12,
  nodePadding = 30,
  onNodeClick,
  autoGrow = true,
  className
}) {
  const fmt = formatValue ?? defaultFormatValue;
  const c = useChartColors();
  const [hovered, setHovered] = React5.useState(null);
  const uid = React5.useId().replace(/:/g, "");
  const idToIndex = React5.useMemo(
    () => Object.fromEntries(nodes.map((n, i) => [n.id, i])),
    [nodes]
  );
  const enrichedNodes = React5.useMemo(
    () => nodes.map((n) => ({ ...n, color: n.color ?? colorFor(n.id) })),
    [nodes]
  );
  const colorById = React5.useMemo(
    () => Object.fromEntries(enrichedNodes.map((n) => [n.id, n.color])),
    [enrichedNodes]
  );
  const idByIndex = React5.useMemo(() => enrichedNodes.map((n) => n.id), [enrichedNodes]);
  const nodeSide = React5.useMemo(() => {
    const src = new Set(links.map((l) => l.source));
    const tgt = new Set(links.map((l) => l.target));
    const m = {};
    for (const n of nodes) {
      const isSrc = src.has(n.id);
      const isTgt = tgt.has(n.id);
      m[n.id] = isSrc && isTgt ? "mid" : isTgt ? "right" : "left";
    }
    return m;
  }, [links, nodes]);
  const enrichedLinks = React5.useMemo(
    () => links.map((l) => ({
      ...l,
      // Recharts wants numeric source/target. Skip links that reference
      // an unknown id rather than crashing — a gentler degrade.
      source: idToIndex[l.source] ?? -1,
      target: idToIndex[l.target] ?? -1,
      // Endpoint colors for the ribbon gradient (resolved from node ids).
      gradFrom: colorById[l.source],
      gradTo: colorById[l.target]
    })).filter((l) => l.source >= 0 && l.target >= 0),
    [links, idToIndex, colorById]
  );
  const graph = React5.useMemo(
    () => ({
      links: enrichedLinks.map((l) => ({
        source: { id: idByIndex[l.source] },
        target: { id: idByIndex[l.target] }
      }))
    }),
    [enrichedLinks, idByIndex]
  );
  const connected = React5.useMemo(() => computeConnected(graph, hovered), [graph, hovered]);
  const nameById = React5.useMemo(
    () => Object.fromEntries(nodes.map((n) => [n.id, n.name])),
    [nodes]
  );
  const tableRows = React5.useMemo(
    () => links.filter((l) => nameById[l.source] && nameById[l.target]),
    [links, nameById]
  );
  const rootTotal = React5.useMemo(() => {
    const incoming = new Set(links.map((l) => l.target));
    return links.filter((l) => !incoming.has(l.source)).reduce((s, l) => s + l.value, 0);
  }, [links]);
  const summary = `Sankey flow diagram: ${nodes.length} nodes, ${links.length} flows, total ${fmt(rootTotal)}.`;
  const autoHeight = React5.useMemo(() => {
    const sourceSet = new Set(links.map((l) => l.source));
    const targetSet = new Set(links.map((l) => l.target));
    let leftOnly = 0, rightOnly = 0, middle = 0;
    for (const id of /* @__PURE__ */ new Set([...sourceSet, ...targetSet])) {
      const isSrc = sourceSet.has(id);
      const isTgt = targetSet.has(id);
      if (isSrc && isTgt) middle += 1;
      else if (isSrc) leftOnly += 1;
      else rightOnly += 1;
    }
    const busiestColumn = Math.max(leftOnly, rightOnly, middle, 1);
    const PER_NODE_PX = nodePadding + 24;
    const V_PADDING = 40;
    const MAX_AUTO_HEIGHT = 1600;
    const natural = autoGrow ? Math.max(height, busiestColumn * PER_NODE_PX + V_PADDING) : height;
    return { natural, capped: Math.min(natural, MAX_AUTO_HEIGHT) };
  }, [links, height, nodePadding, autoGrow]);
  const renderNode = (props) => {
    const { x, y, width, height: h, index, payload, containerWidth } = props;
    const node = enrichedNodes[index] ?? payload;
    const side = nodeSide[node.id] ?? "mid";
    const labelOnRight = side === "right" ? true : side === "left" ? false : x + width + 6 + 110 < containerWidth;
    const labelX = labelOnRight ? x + width + 6 : x - 6;
    const anchor = labelOnRight ? "start" : "end";
    const dim = hovered !== null && !connected.nodes.has(node.id);
    const setOn = () => setHovered(`node:${node.id}`);
    const setOff = () => setHovered(null);
    return /* @__PURE__ */ jsxs(
      "g",
      {
        tabIndex: 0,
        style: {
          cursor: "pointer",
          transition: GEO.transition,
          opacity: dim ? 0.4 : 1,
          outline: "none"
        },
        onClick: onNodeClick ? () => onNodeClick(node) : void 0,
        onMouseEnter: setOn,
        onMouseLeave: setOff,
        onFocus: setOn,
        onBlur: setOff,
        children: [
          /* @__PURE__ */ jsx(
            "rect",
            {
              x,
              y,
              width,
              height: h,
              rx: GEO.nodeCornerRadius,
              fill: node.color
            }
          ),
          /* @__PURE__ */ jsxs(
            "text",
            {
              x: labelX,
              y: y + h / 2,
              dy: "0.35em",
              textAnchor: anchor,
              fontSize: 11,
              fontWeight: 600,
              fill: c.tooltipText,
              children: [
                node.name.length > 16 && /* @__PURE__ */ jsx("title", { children: node.name }),
                truncate(node.name)
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            "text",
            {
              x: labelX,
              y: y + h / 2 + 14,
              textAnchor: anchor,
              fontSize: 10,
              fill: c.tick,
              children: fmt(payload.value ?? 0)
            }
          )
        ]
      },
      `sankey-node-${index}`
    );
  };
  const renderLink = (props) => {
    const { sourceX, sourceY, targetX, targetY, linkWidth, index, payload } = props;
    const ribbon = ribbonPath({
      source: { x1: sourceX },
      target: { x0: targetX },
      y0: sourceY,
      y1: targetY,
      width: linkWidth
    });
    const from = payload.gradFrom ?? c.tick;
    const to = payload.gradTo ?? from;
    const gradId = `${uid}-grad-${index}`;
    const solid = payload.color;
    const opacity = hovered !== null ? connected.links.has(index) ? GEO.linkOpacityHover : GEO.linkOpacityDimmed : GEO.linkOpacity;
    return /* @__PURE__ */ jsxs("g", { children: [
      !solid && /* @__PURE__ */ jsxs(
        "linearGradient",
        {
          id: gradId,
          gradientUnits: "userSpaceOnUse",
          x1: sourceX,
          x2: targetX,
          y1: 0,
          y2: 0,
          children: [
            /* @__PURE__ */ jsx("stop", { offset: "0%", stopColor: from }),
            /* @__PURE__ */ jsx("stop", { offset: "100%", stopColor: to })
          ]
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: ribbon,
          fill: solid ?? `url(#${gradId})`,
          stroke: "none",
          style: { cursor: "grab", transition: GEO.transition, opacity },
          onMouseEnter: () => setHovered(`link:${index}`),
          onMouseLeave: () => setHovered(null)
        }
      )
    ] }, `sankey-link-${index}`);
  };
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        role: "img",
        "aria-label": summary,
        style: {
          width: "100%",
          height: autoHeight.capped,
          overflowY: autoHeight.natural > autoHeight.capped ? "auto" : "visible"
        },
        children: /* @__PURE__ */ jsx("div", { style: { width: "100%", height: autoHeight.natural }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsx(
          Sankey,
          {
            data: { nodes: enrichedNodes, links: enrichedLinks },
            nodePadding,
            nodeWidth,
            margin: { top: 18, right: 122, bottom: 18, left: 122 },
            link: renderLink,
            node: renderNode,
            children: /* @__PURE__ */ jsx(
              Tooltip,
              {
                formatter: (v) => fmt(Number(v)),
                contentStyle: {
                  background: c.tooltipBg,
                  border: `1px solid ${c.tooltipBorder}`,
                  color: c.tooltipText,
                  borderRadius: 8,
                  padding: "10px 12px",
                  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.12)",
                  fontSize: CHART_TYPOGRAPHY.tooltipRowFontSize
                },
                labelStyle: { fontWeight: 600, fontSize: CHART_TYPOGRAPHY.tooltipHeaderFontSize, marginBottom: 4 },
                itemStyle: { color: c.tooltipText, fontSize: CHART_TYPOGRAPHY.tooltipRowFontSize, padding: 0 }
              }
            )
          }
        ) }) })
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "sr-only left-0 top-0", children: /* @__PURE__ */ jsxs("table", { children: [
      /* @__PURE__ */ jsx("caption", { children: summary }),
      /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
        /* @__PURE__ */ jsx("th", { children: "From" }),
        /* @__PURE__ */ jsx("th", { children: "To" }),
        /* @__PURE__ */ jsx("th", { children: "Amount" })
      ] }) }),
      /* @__PURE__ */ jsx("tbody", { children: tableRows.map((l, i) => /* @__PURE__ */ jsxs("tr", { children: [
        /* @__PURE__ */ jsx("td", { children: nameById[l.source] }),
        /* @__PURE__ */ jsx("td", { children: nameById[l.target] }),
        /* @__PURE__ */ jsx("td", { children: fmt(l.value) })
      ] }, `sankey-row-${i}`)) })
    ] }) })
  ] });
}
function EmptyState({
  heading,
  message,
  action,
  className
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2 py-6 text-center", className), children: [
    heading && /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: heading }),
    message && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground/70", children: message }),
    action && /* @__PURE__ */ jsx("div", { className: "pt-2", children: action })
  ] });
}
function Table({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "table-container",
      className: "relative w-full overflow-x-auto",
      children: /* @__PURE__ */ jsx(
        "table",
        {
          "data-slot": "table",
          className: cn("w-full caption-bottom text-sm", className),
          ...props
        }
      )
    }
  );
}
function TableHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "thead",
    {
      "data-slot": "table-header",
      className: cn("bg-muted/50 [&_tr]:border-b", className),
      ...props
    }
  );
}
function TableBody({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "tbody",
    {
      "data-slot": "table-body",
      className: cn("[&_tr:last-child]:border-0", className),
      ...props
    }
  );
}
function TableFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "tfoot",
    {
      "data-slot": "table-footer",
      className: cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        className
      ),
      ...props
    }
  );
}
function TableRow({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "tr",
    {
      "data-slot": "table-row",
      className: cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className
      ),
      ...props
    }
  );
}
function TableHead({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "th",
    {
      "data-slot": "table-head",
      className: cn(
        "h-10 px-2 text-left align-middle font-medium whitespace-nowrap text-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      ),
      ...props
    }
  );
}
function TableCell({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "td",
    {
      "data-slot": "table-cell",
      className: cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      ),
      ...props
    }
  );
}
function TableCaption({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "caption",
    {
      "data-slot": "table-caption",
      className: cn("mt-4 text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "skeleton",
      className: cn("animate-pulse rounded-md bg-accent", className),
      ...props
    }
  );
}
function TableLoadingSkeleton({
  rows = 6,
  cols = 6,
  rowHeight = "h-6",
  className
}) {
  return /* @__PURE__ */ jsx(Fragment, { children: Array.from({ length: rows }).map((_, r) => /* @__PURE__ */ jsx(TableRow, { className, children: Array.from({ length: cols }).map((_2, c) => /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx(Skeleton, { className: cn(rowHeight, "w-full") }) }, c)) }, r)) });
}
var buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
        outline: "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React5.forwardRef(function Button2({ className, variant = "default", size = "default", asChild = false, ...props }, ref) {
  const Comp = asChild ? Slot.Root : "button";
  return /* @__PURE__ */ jsx(
    Comp,
    {
      ref,
      "data-slot": "button",
      "data-variant": variant,
      "data-size": size,
      className: cn(buttonVariants({ variant, size, className })),
      ...props
    }
  );
});
function BulkActionBar({
  count,
  actions,
  onClear,
  className
}) {
  if (count <= 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-slot": "bulk-action-bar",
      className: cn(
        "sticky bottom-0 z-10 flex items-center gap-3 rounded-lg border bg-card px-4 py-2 shadow-sm",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("span", { className: "text-sm font-medium text-foreground", children: [
          count,
          " selected"
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: actions }),
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "ghost",
            size: "sm",
            onClick: onClear,
            className: "ml-auto",
            children: "Clear"
          }
        )
      ]
    }
  );
}
function Checkbox({
  checked,
  onCheckedChange,
  className,
  ...props
}) {
  const ref = React5.useRef(null);
  React5.useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = checked === "indeterminate";
    }
  }, [checked]);
  return /* @__PURE__ */ jsx(
    "input",
    {
      ref,
      type: "checkbox",
      "data-slot": "checkbox",
      checked: checked === true,
      onChange: (e) => onCheckedChange?.(e.currentTarget.checked),
      className: cn(
        "peer h-4 w-4 shrink-0 cursor-pointer rounded-sm border border-input bg-transparent accent-primary outline-none transition-shadow focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props
    }
  );
}
function Input({ className, type, ...props }) {
  return /* @__PURE__ */ jsx(
    "input",
    {
      type,
      "data-slot": "input",
      className: cn(
        "h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30",
        "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
        className
      ),
      ...props
    }
  );
}
function DataTableToolbar({
  search,
  filters,
  actions,
  className
}) {
  return (
    // All controls (search, inline FilterSelects, action buttons) share one
    // height, so a centered single line stays perfectly aligned.
    /* @__PURE__ */ jsxs("div", { className: cn("flex flex-wrap items-center gap-2 mb-3", className), children: [
      search && /* @__PURE__ */ jsxs("div", { className: cn("relative", search.fullWidth ? "flex-1 basis-64" : "w-64"), children: [
        /* @__PURE__ */ jsx(Search, { className: "pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" }),
        /* @__PURE__ */ jsx(
          Input,
          {
            type: "search",
            value: search.value,
            onChange: (e) => search.onChange(e.target.value),
            placeholder: search.placeholder ?? "Search\u2026",
            className: "pl-8"
          }
        )
      ] }),
      filters,
      actions && /* @__PURE__ */ jsx("div", { className: "ml-auto flex items-center gap-2", children: actions })
    ] })
  );
}
function SortableHeader({
  active,
  direction,
  onSort,
  className,
  align = "left",
  children
}) {
  const Arrow = active && direction === "desc" ? ArrowDown : ArrowUp;
  return /* @__PURE__ */ jsx(
    TableHead,
    {
      className: cn(align === "right" && "text-right", className),
      "aria-sort": active ? direction === "asc" ? "ascending" : "descending" : "none",
      children: /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: onSort,
          className: cn(
            // The label must render EXACTLY like a plain TableHead (text-foreground,
            // font-medium, normal case): add NO font/color classes here — the button
            // inherits them from the th (Tailwind preflight: buttons inherit font and
            // color). Active state is carried solely by the arrow opacity.
            "inline-flex items-center gap-1 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 rounded-sm",
            align === "right" && "flex-row-reverse"
          ),
          children: [
            children,
            /* @__PURE__ */ jsx(
              Arrow,
              {
                className: cn(
                  "size-3.5 shrink-0",
                  active ? "opacity-100" : "opacity-40"
                )
              }
            )
          ]
        }
      )
    }
  );
}
function useRowSelection(rows, getId) {
  const [selectedIds, setSelectedIds] = React5.useState(
    () => /* @__PURE__ */ new Set()
  );
  const isSelected = React5.useCallback(
    (id) => selectedIds.has(id),
    [selectedIds]
  );
  const toggle = React5.useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  const clear = React5.useCallback(() => setSelectedIds(/* @__PURE__ */ new Set()), []);
  const currentIds = React5.useMemo(() => rows.map(getId), [rows, getId]);
  const selectedCurrentCount = React5.useMemo(
    () => currentIds.reduce((n, id) => selectedIds.has(id) ? n + 1 : n, 0),
    [currentIds, selectedIds]
  );
  const allSelected = currentIds.length > 0 && selectedCurrentCount === currentIds.length;
  const someSelected = selectedCurrentCount > 0 && !allSelected;
  const toggleAll = React5.useCallback(() => {
    setSelectedIds((prev) => {
      const everyCurrent = currentIds.length > 0 && currentIds.every((id) => prev.has(id));
      if (everyCurrent) {
        const next2 = new Set(prev);
        for (const id of currentIds) next2.delete(id);
        return next2;
      }
      const next = new Set(prev);
      for (const id of currentIds) next.add(id);
      return next;
    });
  }, [currentIds]);
  return {
    selectedIds,
    isSelected,
    toggle,
    toggleAll,
    allSelected,
    someSelected,
    clear,
    count: selectedIds.size
  };
}
function compareValues(a, b) {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}
function useTableSort(rows, columns, initial) {
  const [sort, setSortState] = React5.useState({ key: initial?.key ?? null, dir: initial?.dir ?? "asc" });
  const { key: sortKey, dir: sortDir } = sort;
  const setSort = React5.useCallback((key) => {
    setSortState(
      (prev) => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }, []);
  const sorted = React5.useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col || !col.sortValue) return rows;
    const project = col.sortValue;
    const dirFactor = sortDir === "asc" ? 1 : -1;
    return rows.map((row, i) => ({ row, i })).sort((x, y) => {
      const av = project(x.row);
      const bv = project(y.row);
      const aNull = av === null;
      const bNull = bv === null;
      if (aNull || bNull) {
        if (aNull && bNull) return x.i - y.i;
        return aNull ? 1 : -1;
      }
      const cmp = compareValues(av, bv);
      if (cmp !== 0) return cmp * dirFactor;
      return x.i - y.i;
    }).map((entry) => entry.row);
  }, [rows, columns, sortKey, sortDir]);
  return { sorted, sortKey, sortDir, setSort };
}
function DataTable({
  columns,
  rows,
  getRowId,
  initialSort,
  sortKey: controlledSortKey,
  sortDir: controlledSortDir,
  onSortChange,
  selectable = false,
  bulkActions,
  onRowClick,
  highlightRowId,
  rowActions,
  toolbar,
  stickyHeader = true,
  fillHeight = false,
  emptyText,
  pageSize,
  loadingRows = 6,
  loadingVariant = "skeleton",
  footer
}) {
  const controlled = onSortChange != null;
  const dataRows = rows ?? [];
  const clientSort = useTableSort(dataRows, columns, initialSort);
  const sortedRows = controlled ? dataRows : clientSort.sorted;
  const activeSortKey = controlled ? controlledSortKey ?? null : clientSort.sortKey;
  const activeSortDir = controlled ? controlledSortDir ?? "asc" : clientSort.sortDir;
  const setSort = controlled ? onSortChange : clientSort.setSort;
  const [page, setPage] = React5.useState(1);
  React5.useEffect(() => {
    setPage(1);
  }, [sortedRows.length]);
  const totalPages = pageSize ? Math.max(1, Math.ceil(sortedRows.length / pageSize)) : 1;
  const pagedRows = pageSize ? sortedRows.slice((page - 1) * pageSize, page * pageSize) : sortedRows;
  const selection = useRowSelection(pagedRows, getRowId);
  const { clear } = selection;
  React5.useEffect(() => {
    clear();
  }, [rows, clear]);
  const searchValue = toolbar?.search?.value;
  React5.useEffect(() => {
    clear();
  }, [searchValue, page, clear]);
  React5.useEffect(() => {
    if (!highlightRowId || !pageSize) return;
    const idx = sortedRows.findIndex((r) => getRowId(r) === highlightRowId);
    if (idx >= 0) setPage(Math.floor(idx / pageSize) + 1);
  }, [highlightRowId, sortedRows, pageSize, getRowId]);
  const wrapRef = React5.useRef(null);
  React5.useEffect(() => {
    if (!highlightRowId) return;
    wrapRef.current?.querySelectorAll("[data-rowid]").forEach((el) => {
      if (el.getAttribute("data-rowid") === highlightRowId) el.scrollIntoView({ block: "nearest" });
    });
  }, [highlightRowId, page]);
  const colCount = columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);
  const headerCells = /* @__PURE__ */ jsxs(TableRow, { children: [
    selectable && /* @__PURE__ */ jsx(TableHead, { className: "w-10", children: /* @__PURE__ */ jsx(
      Checkbox,
      {
        "aria-label": "Select all rows on this page",
        checked: selection.allSelected ? true : selection.someSelected ? "indeterminate" : false,
        onCheckedChange: selection.toggleAll
      }
    ) }),
    columns.map(
      (col) => col.sortable ? /* @__PURE__ */ jsx(
        SortableHeader,
        {
          active: activeSortKey === col.key,
          direction: activeSortDir,
          onSort: () => setSort(col.key),
          align: col.align,
          className: col.className,
          children: col.header
        },
        col.key
      ) : /* @__PURE__ */ jsx(
        TableHead,
        {
          className: cn(col.align === "right" && "text-right", col.className),
          children: col.header
        },
        col.key
      )
    ),
    rowActions && /* @__PURE__ */ jsx(TableHead, { className: "text-right", children: /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Actions" }) })
  ] });
  const body = (() => {
    if (rows === null) {
      if (loadingVariant === "spinner") {
        return /* @__PURE__ */ jsx(TableRow, { children: /* @__PURE__ */ jsx(TableCell, { colSpan: colCount, className: "py-16", children: /* @__PURE__ */ jsx("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx(Spinner, {}) }) }) });
      }
      return /* @__PURE__ */ jsx(TableLoadingSkeleton, { rows: loadingRows, cols: colCount });
    }
    if (pagedRows.length === 0) {
      return /* @__PURE__ */ jsx(TableRow, { children: /* @__PURE__ */ jsx(TableCell, { colSpan: colCount, className: "py-0", children: /* @__PURE__ */ jsx(EmptyState, { heading: emptyText ?? "No results." }) }) });
    }
    return pagedRows.map((row) => {
      const id = getRowId(row);
      const selected = selectable && selection.isSelected(id);
      const highlighted = highlightRowId != null && id === highlightRowId;
      return /* @__PURE__ */ jsxs(
        TableRow,
        {
          "data-rowid": id,
          "data-state": selected ? "selected" : void 0,
          className: cn(onRowClick && "cursor-pointer", highlighted && "bg-primary/10"),
          onClick: onRowClick ? () => onRowClick(row) : void 0,
          children: [
            selectable && /* @__PURE__ */ jsx(
              TableCell,
              {
                className: "w-10",
                onClick: (e) => e.stopPropagation(),
                children: /* @__PURE__ */ jsx(
                  Checkbox,
                  {
                    "aria-label": `Select row ${id}`,
                    checked: selection.isSelected(id),
                    onCheckedChange: () => selection.toggle(id)
                  }
                )
              }
            ),
            columns.map((col) => /* @__PURE__ */ jsx(
              TableCell,
              {
                className: cn(
                  col.align === "right" && "text-right tabular-nums",
                  col.className
                ),
                children: col.cell ? col.cell(row) : row[col.key]
              },
              col.key
            )),
            rowActions && /* @__PURE__ */ jsx(
              TableCell,
              {
                className: "text-right",
                onClick: (e) => e.stopPropagation(),
                children: rowActions(row)
              }
            )
          ]
        },
        id
      );
    });
  })();
  const table = /* @__PURE__ */ jsxs(Table, { children: [
    /* @__PURE__ */ jsx(TableHeader, { className: cn(stickyHeader && "sticky top-0 z-10 bg-muted"), children: headerCells }),
    /* @__PURE__ */ jsx(TableBody, { children: body }),
    footer && rows !== null && pagedRows.length > 0 && /* @__PURE__ */ jsx(TableFooter, { children: footer })
  ] });
  return (
    // fillHeight → a flex column that fills the parent's definite height so only the table body
    // scrolls (dashboard tile); otherwise the natural-flow stack with the 70vh viewport cap.
    /* @__PURE__ */ jsxs("div", { className: cn(fillHeight ? "flex h-full min-h-0 flex-col gap-3" : "space-y-3"), ref: wrapRef, children: [
      toolbar && /* @__PURE__ */ jsx(
        DataTableToolbar,
        {
          search: toolbar.search,
          filters: toolbar.filters,
          actions: toolbar.actions
        }
      ),
      selectable && bulkActions && /* @__PURE__ */ jsx(
        BulkActionBar,
        {
          className: "top-0 bottom-auto",
          count: selection.count,
          onClear: selection.clear,
          actions: bulkActions(
            pagedRows.filter((r) => selection.isSelected(getRowId(r))),
            selection.clear
          )
        }
      ),
      stickyHeader ? (
        // Put the vertical scroll on the shadcn table-container ITSELF (the `overflow-x-auto`
        // wrapper) — NOT a separate outer cage. A separate cage nests inside the container's own
        // scroll context, so the sticky <thead> pins to the container (which never scrolled) and
        // the header rides away with the rows. Making the container the single scroll cage (both
        // axes) means the sticky header pins to the element that actually scrolls (AI-ORBIT-341).
        // fillHeight: the cage flexes to the remaining tile height (max-h-none) instead of 70vh, so
        // the body scrolls inside a short tile; else it caps at 70vh of the viewport.
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              fillHeight ? "flex min-h-0 flex-1 flex-col [&_[data-slot=table-container]]:min-h-0 [&_[data-slot=table-container]]:flex-1 [&_[data-slot=table-container]]:overflow-y-auto" : "[&_[data-slot=table-container]]:max-h-[70vh] [&_[data-slot=table-container]]:overflow-y-auto"
            ),
            children: table
          }
        )
      ) : table,
      pageSize && rows !== null && /* @__PURE__ */ jsx(
        Pagination,
        {
          page,
          totalPages,
          onPageChange: setPage,
          summary: `Page ${page} of ${totalPages} \xB7 ${sortedRows.length} rows`
        }
      )
    ] })
  );
}
function DropdownMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenu$1.Root, { "data-slot": "dropdown-menu", ...props });
}
function DropdownMenuPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenu$1.Portal, { "data-slot": "dropdown-menu-portal", ...props });
}
function DropdownMenuTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenu$1.Trigger,
    {
      "data-slot": "dropdown-menu-trigger",
      ...props
    }
  );
}
function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenu$1.Portal, { children: /* @__PURE__ */ jsx(
    DropdownMenu$1.Content,
    {
      "data-slot": "dropdown-menu-content",
      sideOffset,
      className: cn(
        "z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] origin-[var(--radix-dropdown-menu-content-transform-origin)] overflow-x-hidden overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  ) });
}
function DropdownMenuGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenu$1.Group, { "data-slot": "dropdown-menu-group", ...props });
}
function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenu$1.Item,
    {
      "data-slot": "dropdown-menu-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:focus:bg-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:[&>svg]:!text-destructive",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    DropdownMenu$1.CheckboxItem,
    {
      "data-slot": "dropdown-menu-checkbox-item",
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-none select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenu$1.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon, { className: "size-4" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenu$1.RadioGroup,
    {
      "data-slot": "dropdown-menu-radio-group",
      ...props
    }
  );
}
function DropdownMenuRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    DropdownMenu$1.RadioItem,
    {
      "data-slot": "dropdown-menu-radio-item",
      className: cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-none select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenu$1.ItemIndicator, { children: /* @__PURE__ */ jsx(CircleIcon, { className: "size-2 fill-current" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenu$1.Label,
    {
      "data-slot": "dropdown-menu-label",
      "data-inset": inset,
      className: cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenu$1.Separator,
    {
      "data-slot": "dropdown-menu-separator",
      className: cn("-mx-1 my-1 h-px bg-border", className),
      ...props
    }
  );
}
function DropdownMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      "data-slot": "dropdown-menu-shortcut",
      className: cn(
        "ml-auto text-xs tracking-widest text-muted-foreground",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSub({
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenu$1.Sub, { "data-slot": "dropdown-menu-sub", ...props });
}
function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    DropdownMenu$1.SubTrigger,
    {
      "data-slot": "dropdown-menu-sub-trigger",
      "data-inset": inset,
      className: cn(
        "flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none focus:bg-accent focus:text-accent-foreground data-[inset]:pl-8 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(ChevronRightIcon, { className: "ml-auto size-4" })
      ]
    }
  );
}
function DropdownMenuSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenu$1.SubContent,
    {
      "data-slot": "dropdown-menu-sub-content",
      className: cn(
        "z-50 min-w-[8rem] origin-[var(--radix-dropdown-menu-content-transform-origin)] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  );
}
function GridFilterSelect({ label, value, options, onChange }) {
  const [open, setOpen] = React5.useState(false);
  const [query, setQuery] = React5.useState("");
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  const allLabel = `All ${label.toLowerCase()}`;
  function pick(v) {
    onChange(v);
    setOpen(false);
  }
  return /* @__PURE__ */ jsxs(DropdownMenu, { open, onOpenChange: (o) => {
    setOpen(o);
    if (o) setQuery("");
  }, modal: false, children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "outline",
        size: "sm",
        className: `h-8 justify-between gap-1.5 font-normal ${value ? "border-primary/60" : ""}`,
        style: { minWidth: 140, maxWidth: 210 },
        title: value || allLabel,
        children: [
          /* @__PURE__ */ jsx("span", { className: "truncate", children: value || allLabel }),
          /* @__PURE__ */ jsx(ChevronDown, { className: "h-3.5 w-3.5 shrink-0 text-muted-foreground" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "start", className: "w-60 p-0", children: [
      /* @__PURE__ */ jsx("div", { className: "p-2", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 rounded-md border bg-background px-2 py-1.5", children: [
        /* @__PURE__ */ jsx(Search, { className: "h-3.5 w-3.5 shrink-0 text-muted-foreground" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            autoFocus: true,
            value: query,
            onChange: (e) => setQuery(e.target.value),
            onKeyDown: (e) => e.stopPropagation(),
            placeholder: `Filter ${label.toLowerCase()}\u2026`,
            className: "w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          }
        )
      ] }) }),
      /* @__PURE__ */ jsxs("div", { className: "max-h-64 overflow-auto pb-1", children: [
        /* @__PURE__ */ jsx("button", { type: "button", onClick: () => pick(""), className: `flex w-full px-3 py-1.5 text-left text-sm hover:bg-muted/50 ${value ? "" : "font-semibold text-primary"}`, children: allLabel }),
        shown.map((o) => /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: () => pick(o),
            className: `flex w-full truncate px-3 py-1.5 text-left text-sm hover:bg-muted/50 ${o === value ? "font-semibold text-primary" : ""}`,
            children: o
          },
          o
        )),
        shown.length === 0 && /* @__PURE__ */ jsx("div", { className: "px-3 py-4 text-center text-xs text-muted-foreground", children: "No matches" })
      ] })
    ] })
  ] });
}
var alertVariants = cva(
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[1rem_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        // Clearly reads as an error (AI-UI-161): red-tinted fill + red border + red text,
        // not just red text on a plain card.
        destructive: "border-destructive/40 bg-destructive/10 text-destructive *:data-[slot=alert-description]:text-destructive [&>svg]:text-current"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Alert({
  className,
  variant,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "alert",
      role: "alert",
      className: cn(alertVariants({ variant }), className),
      ...props
    }
  );
}
function AlertTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "alert-title",
      className: cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      ),
      ...props
    }
  );
}
function AlertDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "alert-description",
      className: cn(
        "col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed",
        className
      ),
      ...props
    }
  );
}
function formatterForDisplay(numberFormat, measureFmt, decimals) {
  const d = decimals == null ? {} : { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
  switch (numberFormat) {
    case "currency":
      return (n) => formatMoney(n, d);
    case "compact":
      return (n) => formatMoney(n, { notation: "compact", ...decimals == null ? {} : { maximumFractionDigits: decimals } });
    case "plain":
      return (n) => formatNumber(n, d);
    default:
      if (decimals == null) return formatterFor(measureFmt);
      if (measureFmt === "currency" || measureFmt === "price") return (n) => formatMoney(n, d);
      if (measureFmt === "percent") return (n) => `${formatNumber(n, d)}%`;
      return (n) => formatNumber(n, d);
  }
}
function formatterFor(fmt) {
  switch (fmt) {
    case "currency":
    case "price":
      return (n) => formatMoney(n);
    case "percent":
      return (n) => `${formatNumber(n)}%`;
    default:
      return (n) => formatNumber(n);
  }
}
function groupLabel(g, v) {
  if (v == null) return "\u2014";
  if (g?.is_time) return formatTimeBucket(v, g.grain);
  return String(v);
}
function toPoints(data) {
  const dim = data.group_by[0];
  return data.rows.filter((r) => r.value != null).map((r) => ({
    label: dim != null ? groupLabel(dim, r[dim.key]) : "\u2014",
    value: Number(r.value)
  }));
}
function toSeries(pivot) {
  const alias = new Map(pivot.series.map((s, i) => [s.key, `s${i}`]));
  return {
    rows: pivot.rows.map((r) => {
      const values = {};
      for (const [k, v] of Object.entries(r.values)) values[alias.get(k) ?? k] = v;
      return { label: r.label, values };
    }),
    series: pivot.series.map((s) => ({ key: alias.get(s.key), name: s.name }))
  };
}
var STACKED_MAX_SERIES = 10;
var SERIES_OTHER_KEY = "__other__";
function capSeries(rows, series, max = STACKED_MAX_SERIES) {
  if (series.length <= max) return { rows, series };
  const total = /* @__PURE__ */ new Map();
  for (const s of series) {
    let t = 0;
    for (const r of rows) t += Number(r.values[s.key] ?? 0);
    total.set(s.key, t);
  }
  const keepKeys = /* @__PURE__ */ new Set();
  for (const r of rows) {
    let bestKey;
    let bestVal = 0;
    for (const [k, v] of Object.entries(r.values)) {
      const n = Number(v ?? 0);
      if (n > bestVal) {
        bestVal = n;
        bestKey = k;
      }
    }
    if (bestKey != null) keepKeys.add(bestKey);
  }
  for (const s of [...series].sort((a, b) => (total.get(b.key) ?? 0) - (total.get(a.key) ?? 0))) {
    if (keepKeys.size >= max) break;
    keepKeys.add(s.key);
  }
  const keep = series.filter((s) => keepKeys.has(s.key)).sort((a, b) => (total.get(b.key) ?? 0) - (total.get(a.key) ?? 0));
  const cappedRows = rows.map((r) => {
    const values = {};
    let other = 0;
    for (const [k, v] of Object.entries(r.values)) {
      if (keepKeys.has(k)) values[k] = v;
      else other += Number(v ?? 0);
    }
    if (other) values[SERIES_OTHER_KEY] = other;
    return { label: r.label, values };
  });
  return {
    rows: cappedRows,
    series: [...keep, { key: SERIES_OTHER_KEY, name: "Others", color: colorFor("(other)") }]
  };
}
var SANKEY_MAX_PER_LEVEL = 12;
var SANKEY_OTHER_LABEL = "Others";
function sankeyLevelOf(id) {
  const m = /^L(\d+):/.exec(id);
  return m ? Number(m[1]) : 0;
}
function capSankeyPerLevel(nodes, links, perLevel = SANKEY_MAX_PER_LEVEL) {
  const throughput = /* @__PURE__ */ new Map();
  for (const l of links) {
    throughput.set(l.source, (throughput.get(l.source) ?? 0) + l.value);
    throughput.set(l.target, (throughput.get(l.target) ?? 0) + l.value);
  }
  const byLevel = /* @__PURE__ */ new Map();
  for (const n of nodes) {
    const lvl = sankeyLevelOf(n.id);
    const arr = byLevel.get(lvl);
    if (arr) arr.push(n);
    else byLevel.set(lvl, [n]);
  }
  const remap = /* @__PURE__ */ new Map();
  const otherIds = /* @__PURE__ */ new Set();
  for (const [lvl, lvlNodes] of byLevel) {
    if (lvlNodes.length <= perLevel) {
      for (const n of lvlNodes) remap.set(n.id, n.id);
      continue;
    }
    const otherId = `L${lvl}:${SANKEY_OTHER_LABEL}`;
    [...lvlNodes].sort((a, b) => (throughput.get(b.id) ?? 0) - (throughput.get(a.id) ?? 0)).forEach((n, i) => remap.set(n.id, i < perLevel ? n.id : otherId));
    otherIds.add(otherId);
  }
  const out = [];
  const emitted = /* @__PURE__ */ new Set();
  for (const n of nodes) {
    if (remap.get(n.id) === n.id && !emitted.has(n.id)) {
      out.push(n);
      emitted.add(n.id);
    }
  }
  for (const oid of otherIds) {
    if (!emitted.has(oid)) {
      out.push({ id: oid, name: SANKEY_OTHER_LABEL });
      emitted.add(oid);
    }
  }
  const agg = /* @__PURE__ */ new Map();
  for (const l of links) {
    const s = remap.get(l.source) ?? l.source;
    const t = remap.get(l.target) ?? l.target;
    if (s === t) continue;
    const k = `${s}	${t}`;
    const existing = agg.get(k);
    if (existing) existing.value += l.value;
    else agg.set(k, { source: s, target: t, value: l.value });
  }
  const cappedLinks = Array.from(agg.values());
  return { nodes: out, links: cappedLinks };
}
function toSankey(data) {
  const levels = data.group_by.map((g) => g.key);
  const seen = /* @__PURE__ */ new Set();
  const nodes = [];
  const linkValue = /* @__PURE__ */ new Map();
  const addNode = (lvl, val) => {
    const id = `L${lvl}:${val}`;
    if (!seen.has(id)) {
      seen.add(id);
      nodes.push({ id, name: val });
    }
    return id;
  };
  for (const r of data.rows) {
    if (r.value == null) continue;
    const v = Number(r.value);
    if (v <= 0) continue;
    for (let i = 0; i < levels.length - 1; i++) {
      const a = addNode(i, String(r[levels[i]] ?? "\u2014"));
      const b = addNode(i + 1, String(r[levels[i + 1]] ?? "\u2014"));
      const key = `${a}\0${b}`;
      linkValue.set(key, (linkValue.get(key) ?? 0) + v);
    }
  }
  const links = Array.from(linkValue.entries()).map(([k, value]) => {
    const [source, target] = k.split("\0");
    return { source, target, value };
  });
  const capped = capSankeyPerLevel(nodes, links);
  const keptLinks = capped.links.filter((l) => l.value > 0);
  const used = /* @__PURE__ */ new Set();
  keptLinks.forEach((l) => {
    used.add(l.source);
    used.add(l.target);
  });
  return { nodes: capped.nodes.filter((n) => used.has(n.id)), links: keptLinks };
}
function DataWindow({
  data,
  renderer,
  loading,
  error,
  height = 240,
  formatValue,
  numberFormat,
  dataLabels,
  legend,
  axisTitle,
  gridlines,
  smooth,
  showPoints,
  autoGrow,
  xLabels,
  catInterval,
  decimals,
  axisMin,
  axisMax,
  axisTicks,
  targetLine,
  legendPos,
  condOn,
  condOp,
  condValue,
  condColor,
  emptyMessage,
  hideKpiLabel,
  onSegmentClick,
  onRowInspect,
  highlightValue,
  gridExtraColumns,
  gridPageSize,
  gridSearch,
  gridTotals,
  gridFilters,
  gridHiddenCols,
  gridCellIcon,
  groupOthers,
  barWidth,
  pieLabel,
  legendValues,
  className
}) {
  const [gridQuery, setGridQuery] = React5.useState("");
  const [colFilters, setColFilters] = React5.useState({});
  if (error) {
    return /* @__PURE__ */ jsx(Alert, { variant: "destructive", className, children: /* @__PURE__ */ jsx(AlertDescription, { children: error }) });
  }
  if (loading || data == null) {
    const isKpi = renderer === "kpi-card";
    return /* @__PURE__ */ jsx(
      "div",
      {
        className,
        style: isKpi ? { display: "flex", justifyContent: "center", padding: "18px 0" } : { height, display: "flex", alignItems: "center", justifyContent: "center" },
        children: /* @__PURE__ */ jsx(
          "div",
          {
            className: `animate-spin rounded-full border-2 border-muted border-t-primary ${isKpi ? "h-6 w-6" : "h-8 w-8"}`,
            role: "status",
            "aria-label": "Loading"
          }
        )
      }
    );
  }
  if (!data.rows || data.rows.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { heading: emptyMessage ?? "No data for this view." });
  }
  if (groupOthers === false && data.group_by.length === 1) {
    const otherKey = data.group_by[0].key;
    data = { ...data, rows: data.rows.filter((r) => String(r[otherKey] ?? "") !== "(other)") };
  }
  const fmt = formatValue ?? formatterForDisplay(numberFormat, data.measure.fmt, decimals);
  const condColorFn = condOn && condValue != null ? (v) => {
    const hit = condOp === "lt" ? v < condValue : v > condValue;
    const map = { amber: "#d97706", red: "#dc2626", green: "#16a34a" };
    return hit ? map[condColor ?? "amber"] ?? map.amber : void 0;
  } : void 0;
  const primaryKey = data.group_by[0]?.key;
  const selectByLabel = onSegmentClick && primaryKey ? (label) => onSegmentClick({ key: primaryKey, value: label }) : void 0;
  const selectSankeyNode = onSegmentClick ? (node) => {
    const m = /^L(\d+):(.*)$/.exec(node.id);
    if (!m) return;
    const key = data.group_by[Number(m[1])]?.key;
    if (key) onSegmentClick({ key, value: m[2] });
  } : void 0;
  switch (renderer) {
    case "kpi-card": {
      const row0 = data.rows[0] ?? {};
      const raw = row0.value;
      const prior = row0.prior_value;
      let delta;
      let deltaSign;
      if (raw != null && prior != null && Number(prior) !== 0) {
        const pct = (Number(raw) - Number(prior)) / Math.abs(Number(prior)) * 100;
        deltaSign = pct;
        delta = `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}% vs prior`;
      }
      const goodDir = data.measure.fmt === "currency" || data.measure.fmt === "price" ? "down" : "up";
      return /* @__PURE__ */ jsx(
        KpiCard,
        {
          label: hideKpiLabel ? void 0 : data.measure.label,
          value: raw == null ? "\u2014" : fmt(Number(raw)),
          delta,
          deltaSign,
          deltaIsGood: goodDir,
          bare: hideKpiLabel
        }
      );
    }
    case "bar":
      return /* @__PURE__ */ jsx(BarChart, { data: toPoints(data), height, formatValue: fmt, className, onSelect: selectByLabel, showValues: dataLabels, axisTitle, showGrid: gridlines !== false, yMin: axisMin, yMax: axisMax, valueTicks: axisTicks, targetLine, pointColor: condColorFn, barWidth, xLabels, catInterval });
    case "horizontal-bar":
      return /* @__PURE__ */ jsx(HorizontalBarChart, { data: toPoints(data), height, formatValue: fmt, className, onSelect: selectByLabel, showValues: dataLabels, axisTitle, showGrid: gridlines !== false, yMin: axisMin, yMax: axisMax, valueTicks: axisTicks, targetLine, pointColor: condColorFn, barWidth, xLabels, catInterval });
    case "line":
      return /* @__PURE__ */ jsx(LineChart, { data: toPoints(data), height, formatValue: fmt, className, onSelect: selectByLabel, showValues: dataLabels, axisTitle, showGrid: gridlines !== false, yMin: axisMin, yMax: axisMax, valueTicks: axisTicks, targetLine, smooth, showDots: showPoints });
    case "area":
      return /* @__PURE__ */ jsx(AreaChart, { data: toPoints(data), height, formatValue: fmt, className, onSelect: selectByLabel, showValues: dataLabels, axisTitle, showGrid: gridlines !== false, yMin: axisMin, yMax: axisMax, valueTicks: axisTicks, targetLine, smooth });
    case "pie":
    case "donut":
      return /* @__PURE__ */ jsx(PieChart, { data: toPoints(data), height, formatValue: fmt, donut: renderer === "donut", className, onSelect: selectByLabel, showValues: dataLabels, labelMode: pieLabel === "value" ? "value" : "percent", legendValues, showLegend: legend !== false, legendPos });
    case "stacked-bar": {
      if (!data.pivot) return /* @__PURE__ */ jsx(BarChart, { data: toPoints(data), height, formatValue: fmt, className, onSelect: selectByLabel, showValues: dataLabels, axisTitle, showGrid: gridlines !== false, yMin: axisMin, yMax: axisMax, valueTicks: axisTicks, targetLine, pointColor: condColorFn, barWidth, xLabels, catInterval });
      const _pv = toSeries(data.pivot);
      const { rows, series } = capSeries(_pv.rows, _pv.series);
      return /* @__PURE__ */ jsx(StackedBarChart, { data: rows, series, height, formatValue: fmt, className, xLabels, catInterval, valueTicks: axisTicks });
    }
    case "grouped-bar": {
      if (!data.pivot) return /* @__PURE__ */ jsx(BarChart, { data: toPoints(data), height, formatValue: fmt, className, onSelect: selectByLabel, showValues: dataLabels, axisTitle, showGrid: gridlines !== false, yMin: axisMin, yMax: axisMax, valueTicks: axisTicks, targetLine, pointColor: condColorFn, barWidth, xLabels, catInterval });
      const _pv = toSeries(data.pivot);
      const { rows, series } = capSeries(_pv.rows, _pv.series);
      return /* @__PURE__ */ jsx(StackedBarChart, { grouped: true, data: rows, series, height, formatValue: fmt, className, xLabels, catInterval, valueTicks: axisTicks });
    }
    case "multi-line": {
      if (!data.pivot) return /* @__PURE__ */ jsx(LineChart, { data: toPoints(data), height, formatValue: fmt, className, onSelect: selectByLabel, showValues: dataLabels, axisTitle, showGrid: gridlines !== false, yMin: axisMin, yMax: axisMax, valueTicks: axisTicks, targetLine, smooth, showDots: showPoints });
      const _pv = toSeries(data.pivot);
      const { rows, series } = capSeries(_pv.rows, _pv.series);
      return /* @__PURE__ */ jsx(MultiSeriesLineChart, { data: rows, series, height, formatValue: fmt, className, xLabels, catInterval, valueTicks: axisTicks });
    }
    case "sankey": {
      const { nodes, links } = toSankey(data);
      return /* @__PURE__ */ jsx(SankeyChart, { nodes, links, height, formatValue: fmt, className, onNodeClick: selectSankeyNode, autoGrow });
    }
    case "data-grid":
    default: {
      const indexed = data.rows.map((r, i) => ({ ...r, __i: i }));
      const primaryDim = data.group_by[0];
      const hlIdx = highlightValue != null && primaryDim ? data.rows.findIndex((r) => (primaryDim.is_time ? groupLabel(primaryDim, r[primaryDim.key]) : String(r[primaryDim.key] ?? "")) === highlightValue) : -1;
      const highlightRowId = hlIdx >= 0 ? String(hlIdx) : void 0;
      const cols = [
        ...data.group_by.map((g) => ({
          key: g.key,
          header: g.label,
          // Client-side clickable-column sort (task 3715 follow-up): dimension columns sort by
          // their display label. Default (unsorted) keeps the server's hierarchical order.
          sortable: true,
          // Time buckets sort by their RAW value (ISO date sorts chronologically), not the
          // formatted label ("Apr 2026" < "Feb 2026" alphabetically would scramble the axis).
          // null/sentinel-less rows sort last. (task 3715 follow-up — Fable review)
          sortValue: (r) => {
            const raw = r[g.key];
            if (raw == null) return null;
            return g.is_time ? String(raw) : groupLabel(g, r[g.key]);
          },
          cell: (r) => {
            const label = groupLabel(g, r[g.key]);
            const icon = gridCellIcon?.(g.key, String(r[g.key] ?? ""));
            if (!icon) return label;
            if (g.key === "provider") {
              const name = String(r[g.key] ?? "");
              return /* @__PURE__ */ jsx("span", { className: "inline-flex items-center", title: name, children: /* @__PURE__ */ jsx("img", { src: icon, alt: name, className: "h-6 w-6 shrink-0 object-contain" }) });
            }
            return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
              /* @__PURE__ */ jsx("img", { src: icon, alt: "", className: "h-5 w-5 shrink-0 object-contain" }),
              label
            ] });
          }
        })),
        {
          key: "value",
          header: data.measure.label,
          align: "right",
          // Numeric sort so a header click gives a clean biggest/smallest-first list across the
          // whole loaded set (sorted before pagination in DataTable); nulls sort last (task 3715).
          sortable: true,
          sortValue: (r) => r.value == null ? null : Number(r.value),
          cell: (r) => {
            if (r.value == null) return "\u2014";
            const col = condColorFn?.(Number(r.value));
            return col ? /* @__PURE__ */ jsx("span", { style: { color: col, fontWeight: 600 }, children: fmt(Number(r.value)) }) : fmt(Number(r.value));
          }
        },
        ...gridExtraColumns ?? []
      ];
      const gq = gridQuery.trim().toLowerCase();
      const gridRows = gq ? indexed.filter((r) => data.group_by.some((g) => groupLabel(g, r[g.key]).toLowerCase().includes(gq))) : indexed;
      const visibleCols = gridHiddenCols && gridHiddenCols.length ? cols.filter((c) => !gridHiddenCols.includes(c.key)) : cols;
      const visibleColKeys = new Set(visibleCols.map((c) => c.key));
      const activeColFilters = Object.entries(colFilters).filter(([k, v]) => v.trim() && visibleColKeys.has(k));
      const gridRows2 = activeColFilters.length ? gridRows.filter((r) => activeColFilters.every(([k, v]) => {
        const g = data.group_by.find((x) => x.key === k);
        const cell = k === "value" ? r.value == null ? "" : fmt(Number(r.value)) : g ? groupLabel(g, r[k]) : String(r[k] ?? "");
        return cell === v;
      })) : gridRows;
      const grandTotal = data.rows.reduce((acc, r) => acc + (r.value == null ? 0 : Number(r.value)), 0);
      const totalsFooter = gridTotals ? /* @__PURE__ */ jsx(TableRow, { children: visibleCols.map((c, i) => /* @__PURE__ */ jsx(
        TableCell,
        {
          className: c.key === "value" ? "text-right font-semibold" : "font-semibold",
          children: c.key === "value" ? fmt(grandTotal) : i === 0 ? "Total" : ""
        },
        c.key
      )) }) : void 0;
      const filterBar = gridFilters ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2 py-1", children: visibleCols.filter((c) => c.key !== "value" && data.group_by.some((x) => x.key === c.key)).map((c) => {
        const g = data.group_by.find((x) => x.key === c.key);
        const opts = [...new Set(indexed.map((r) => g ? groupLabel(g, r[c.key]) : String(r[c.key] ?? "")))].filter(Boolean).sort();
        if (opts.length === 0) return null;
        return /* @__PURE__ */ jsx(
          GridFilterSelect,
          {
            label: typeof c.header === "string" ? c.header : c.key,
            value: colFilters[c.key] ?? "",
            options: opts,
            onChange: (v) => setColFilters((cur) => ({ ...cur, [c.key]: v }))
          },
          c.key
        );
      }) }) : void 0;
      const gridToolbar = gridSearch || gridFilters ? { search: gridSearch ? { value: gridQuery, onChange: setGridQuery, placeholder: "Search rows\u2026" } : void 0, filters: filterBar } : void 0;
      return /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: visibleCols,
          rows: gridRows2,
          footer: totalsFooter,
          getRowId: (r) => String(r.__i),
          highlightRowId,
          pageSize: gridPageSize,
          fillHeight: autoGrow === false,
          toolbar: gridToolbar,
          onRowClick: onRowInspect ? (r) => onRowInspect(r) : onSegmentClick ? (r) => {
            const k = data.group_by[0]?.key;
            if (k) onSegmentClick({ key: k, value: String(r[k] ?? "") });
          } : void 0
        }
      );
    }
  }
}
function Sheet({ ...props }) {
  return /* @__PURE__ */ jsx(Dialog$1.Root, { "data-slot": "sheet", ...props });
}
function SheetTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx(Dialog$1.Trigger, { "data-slot": "sheet-trigger", ...props });
}
function SheetClose({
  ...props
}) {
  return /* @__PURE__ */ jsx(Dialog$1.Close, { "data-slot": "sheet-close", ...props });
}
function SheetPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx(Dialog$1.Portal, { "data-slot": "sheet-portal", ...props });
}
function SheetOverlay({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Dialog$1.Overlay,
    {
      "data-slot": "sheet-overlay",
      className: cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
}
function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs(SheetPortal, { children: [
    /* @__PURE__ */ jsx(SheetOverlay, {}),
    /* @__PURE__ */ jsxs(
      Dialog$1.Content,
      {
        "data-slot": "sheet-content",
        className: cn(
          "fixed z-50 flex flex-col gap-4 bg-background shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-500",
          side === "right" && "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
          side === "left" && "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
          side === "top" && "inset-x-0 top-0 h-auto border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
          side === "bottom" && "inset-x-0 bottom-0 h-auto border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          className
        ),
        ...props,
        children: [
          children,
          showCloseButton && /* @__PURE__ */ jsxs(Dialog$1.Close, { className: "absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-secondary", children: [
            /* @__PURE__ */ jsx(XIcon, { className: "size-4" }),
            /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
          ] })
        ]
      }
    )
  ] });
}
function SheetHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "sheet-header",
      className: cn("flex flex-col gap-1.5 p-4", className),
      ...props
    }
  );
}
function SheetFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "sheet-footer",
      className: cn("mt-auto flex flex-col gap-2 p-4", className),
      ...props
    }
  );
}
function SheetTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Dialog$1.Title,
    {
      "data-slot": "sheet-title",
      className: cn("font-semibold text-foreground", className),
      ...props
    }
  );
}
function SheetDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Dialog$1.Description,
    {
      "data-slot": "sheet-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function InspectorPanel({
  open,
  onOpenChange,
  title,
  subtitle,
  headline,
  sections,
  loading,
  error,
  emptyMessage
}) {
  const isEmpty = !loading && !error && !headline && sections.every((s) => s.fields.length === 0);
  return /* @__PURE__ */ jsx(Sheet, { open, onOpenChange, children: /* @__PURE__ */ jsxs(SheetContent, { side: "right", className: "flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-md", children: [
    /* @__PURE__ */ jsxs(SheetHeader, { className: "gap-1 border-b bg-muted/30 px-6 py-5", children: [
      /* @__PURE__ */ jsx(SheetTitle, { className: "truncate text-lg font-semibold tracking-tight", children: title }),
      subtitle ? /* @__PURE__ */ jsx(SheetDescription, { className: "truncate", children: subtitle }) : null
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-6 px-6 py-5", children: [
      loading ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Loading\u2026" }) : null,
      error ? /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error }) : null,
      isEmpty ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: emptyMessage ?? "No details." }) : null,
      !loading && !error && headline ? /* @__PURE__ */ jsxs("div", { className: "rounded-xl border bg-card p-4 shadow-sm", children: [
        /* @__PURE__ */ jsx("div", { className: "text-[11px] font-semibold uppercase tracking-wider text-muted-foreground", children: headline.label }),
        /* @__PURE__ */ jsx("div", { className: "mt-1 font-display text-[2rem] font-semibold leading-none tracking-tight tabular-nums text-card-foreground", children: headline.value }),
        headline.sub ? /* @__PURE__ */ jsx("div", { className: "mt-2 text-xs text-muted-foreground", children: headline.sub }) : null
      ] }) : null,
      !loading && !error ? sections.map(
        (section, i) => section.fields.length === 0 ? null : /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
          section.heading ? /* @__PURE__ */ jsx("div", { className: "text-[11px] font-semibold uppercase tracking-wider text-muted-foreground", children: section.heading }) : null,
          /* @__PURE__ */ jsx("dl", { className: "divide-y rounded-lg border", children: section.fields.map((f) => /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-4 px-3 py-2.5", children: [
            /* @__PURE__ */ jsx("dt", { className: "text-sm text-muted-foreground", children: f.label }),
            /* @__PURE__ */ jsxs("dd", { className: "flex flex-col items-end text-right", children: [
              /* @__PURE__ */ jsx("span", { className: "text-sm font-medium tabular-nums text-foreground", children: f.value }),
              f.hint ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: f.hint }) : null
            ] })
          ] }, f.label)) })
        ] }, section.heading ?? i)
      ) : null
    ] })
  ] }) });
}
var FilterContext = React5.createContext(null);
function FilterProvider({ initial, onChange, children }) {
  const [filters, setFilters] = React5.useState(initial ?? {});
  const onChangeRef = React5.useRef(onChange);
  onChangeRef.current = onChange;
  const filtersRef = React5.useRef(filters);
  const apply = React5.useCallback((fn) => {
    const next = fn(filtersRef.current);
    filtersRef.current = next;
    setFilters(next);
    onChangeRef.current?.(next);
  }, []);
  const value = React5.useMemo(
    () => ({
      filters,
      setFilter: (key, v) => apply((prev) => ({ ...prev, [key]: v })),
      removeFilter: (key) => apply((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      }),
      clear: () => apply(() => ({}))
    }),
    [filters, apply]
  );
  return /* @__PURE__ */ jsx(FilterContext.Provider, { value, children });
}
function useFilters() {
  const ctx = React5.useContext(FilterContext);
  if (!ctx) throw new Error("useFilters must be used within a <FilterProvider>");
  return ctx;
}
function describeValue(v) {
  if (Array.isArray(v)) {
    if (v.length === 2 && v.every((x) => x === null || typeof x === "number")) {
      const [lo, hi] = v;
      return `${lo ?? "\u2212\u221E"}\u2026${hi ?? "\u221E"}`;
    }
    return v.join(", ");
  }
  if (v && typeof v === "object") return `${v.key} = ${v.value}`;
  return String(v);
}
function FilterBar({ children, className }) {
  const { filters, removeFilter, clear } = useFilters();
  const entries = Object.entries(filters);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-wrap items-center gap-2", className), children: [
    children,
    entries.map(([k, v]) => /* @__PURE__ */ jsxs(Badge, { variant: "secondary", className: "gap-1", children: [
      /* @__PURE__ */ jsxs("span", { children: [
        k,
        ": ",
        describeValue(v)
      ] }),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          "aria-label": `Remove filter ${k}`,
          className: "ml-1 rounded-full px-1 text-muted-foreground hover:text-foreground",
          onClick: () => removeFilter(k),
          children: "\xD7"
        }
      )
    ] }, k)),
    entries.length > 0 && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", onClick: clear, children: "Reset" })
  ] });
}
var DEFAULT_DATE_PRESETS = [
  { key: "this_month", label: "This month" },
  { key: "last_30d", label: "Last 30 days" },
  { key: "last_90d", label: "Last 90 days" },
  { key: "last_12_months", label: "Last 12 months" },
  { key: "this_year", label: "YTD" },
  { key: "all", label: "All time" }
];
function FilterDateRange({
  filterKey,
  presets = DEFAULT_DATE_PRESETS
}) {
  const { filters, setFilter, removeFilter } = useFilters();
  const cur = filters[filterKey];
  return /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1", children: presets.map((p) => /* @__PURE__ */ jsx(
    Button,
    {
      size: "sm",
      variant: cur === p.key || p.key === "all" && cur == null ? "default" : "outline",
      onClick: () => p.key === "all" ? removeFilter(filterKey) : setFilter(filterKey, p.key),
      children: p.label
    },
    p.key
  )) });
}
function FilterMultiSelect({
  filterKey,
  label,
  options
}) {
  const { filters, setFilter, removeFilter } = useFilters();
  const selected = filters[filterKey] ?? [];
  const [query, setQuery] = React5.useState("");
  const sorted = React5.useMemo(
    () => [...options].sort((a, b) => a.label.localeCompare(b.label)),
    [options]
  );
  const shown = React5.useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sorted.filter((o) => o.label.toLowerCase().includes(q)) : sorted;
  }, [sorted, query]);
  function setSelection(next) {
    if (next.length === 0) removeFilter(filterKey);
    else setFilter(filterKey, next);
  }
  function toggle(v) {
    setSelection(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  }
  function selectAllShown() {
    setSelection(Array.from(/* @__PURE__ */ new Set([...selected, ...shown.map((o) => o.value)])));
  }
  const MAX_SHOWN = 2;
  const selectedLabels = selected.map((v) => options.find((o) => o.value === v)?.label ?? v);
  const summary = selectedLabels.length === 0 ? "" : selectedLabels.length <= MAX_SHOWN ? `: ${selectedLabels.join(", ")}` : `: ${selectedLabels.slice(0, MAX_SHOWN).join(", ")} +${selectedLabels.length - MAX_SHOWN}`;
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", className: "max-w-[18rem]", title: selectedLabels.join(", ") || void 0, children: /* @__PURE__ */ jsx("span", { className: "truncate", children: `${label}${summary}` }) }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { className: "w-64 p-0", children: [
      /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "pb-1", children: label }),
      /* @__PURE__ */ jsx("div", { className: "px-2 pb-1", children: /* @__PURE__ */ jsx(
        Input,
        {
          placeholder: `Search ${label.toLowerCase()}\u2026`,
          value: query,
          onChange: (e) => setQuery(e.target.value),
          onKeyDown: (e) => e.stopPropagation(),
          className: "h-8"
        }
      ) }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-2 pb-1", children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "ghost",
            size: "sm",
            className: "h-7 px-2 text-xs",
            onClick: selectAllShown,
            disabled: shown.length === 0,
            children: query.trim() ? "Select all (filtered)" : "Select all"
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "ghost",
            size: "sm",
            className: "h-7 px-2 text-xs",
            onClick: () => removeFilter(filterKey),
            disabled: selected.length === 0,
            children: "Clear all"
          }
        )
      ] }),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      /* @__PURE__ */ jsx("div", { className: "max-h-56 overflow-auto", children: shown.length === 0 ? /* @__PURE__ */ jsx("div", { className: "px-2 py-3 text-center text-xs text-muted-foreground", children: "No matches" }) : shown.map((o) => /* @__PURE__ */ jsx(
        DropdownMenuCheckboxItem,
        {
          checked: selected.includes(o.value),
          onCheckedChange: () => toggle(o.value),
          onSelect: (e) => e.preventDefault(),
          children: o.label
        },
        o.value
      )) })
    ] })
  ] });
}
function FilterNumberRange({
  filterKey,
  label
}) {
  const { filters, setFilter, removeFilter } = useFilters();
  const [lo, hi] = filters[filterKey] ?? [null, null];
  function update(nextLo, nextHi) {
    if (nextLo == null && nextHi == null) removeFilter(filterKey);
    else setFilter(filterKey, [nextLo, nextHi]);
  }
  const parse = (s) => s === "" ? null : Number(s);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
    /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx(
      Input,
      {
        type: "number",
        className: "h-8 w-24",
        placeholder: "min",
        value: lo ?? "",
        onChange: (e) => update(parse(e.target.value), hi)
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2013" }),
    /* @__PURE__ */ jsx(
      Input,
      {
        type: "number",
        className: "h-8 w-24",
        placeholder: "max",
        value: hi ?? "",
        onChange: (e) => update(lo, parse(e.target.value))
      }
    )
  ] });
}
function FilterTagInput({
  filterKey,
  label = "Tag"
}) {
  const { setFilter } = useFilters();
  const [k, setK] = React5.useState("");
  const [v, setV] = React5.useState("");
  function add() {
    if (k && v) {
      setFilter(filterKey, { key: k, value: v });
      setK("");
      setV("");
    }
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
    /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx(Input, { className: "h-8 w-28", placeholder: "key", value: k, onChange: (e) => setK(e.target.value) }),
    /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "=" }),
    /* @__PURE__ */ jsx(
      Input,
      {
        className: "h-8 w-28",
        placeholder: "value",
        value: v,
        onChange: (e) => setV(e.target.value),
        onKeyDown: (e) => e.key === "Enter" && add()
      }
    ),
    /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: add, disabled: !k || !v, children: "Add" })
  ] });
}
function Collapsible({
  ...props
}) {
  return /* @__PURE__ */ jsx(Collapsible$1.Root, { "data-slot": "collapsible", ...props });
}
function CollapsibleTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Collapsible$1.CollapsibleTrigger,
    {
      "data-slot": "collapsible-trigger",
      ...props
    }
  );
}
function CollapsibleContent({
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Collapsible$1.CollapsibleContent,
    {
      "data-slot": "collapsible-content",
      ...props
    }
  );
}
function Dialog({
  ...props
}) {
  return /* @__PURE__ */ jsx(Dialog$1.Root, { "data-slot": "dialog", ...props });
}
function DialogTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx(Dialog$1.Trigger, { "data-slot": "dialog-trigger", ...props });
}
function DialogPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx(Dialog$1.Portal, { "data-slot": "dialog-portal", ...props });
}
function DialogClose({
  ...props
}) {
  return /* @__PURE__ */ jsx(Dialog$1.Close, { "data-slot": "dialog-close", ...props });
}
function DialogOverlay({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Dialog$1.Overlay,
    {
      "data-slot": "dialog-overlay",
      className: cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
}
function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs(DialogPortal, { "data-slot": "dialog-portal", children: [
    /* @__PURE__ */ jsx(DialogOverlay, {}),
    /* @__PURE__ */ jsxs(
      Dialog$1.Content,
      {
        "data-slot": "dialog-content",
        className: cn(
          // Cap height to the viewport and scroll internally — a tall dialog
          // must not push its top/bottom off-screen with no way to reach them.
          "fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-lg border bg-background p-6 shadow-lg duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-lg",
          className
        ),
        ...props,
        children: [
          children,
          showCloseButton && /* @__PURE__ */ jsxs(
            Dialog$1.Close,
            {
              "data-slot": "dialog-close",
              className: "absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
              children: [
                /* @__PURE__ */ jsx(XIcon, {}),
                /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
              ]
            }
          )
        ]
      }
    )
  ] });
}
function DialogHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "dialog-header",
      className: cn("flex flex-col gap-2 text-center sm:text-left", className),
      ...props
    }
  );
}
function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-slot": "dialog-footer",
      className: cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      ),
      ...props,
      children: [
        children,
        showCloseButton && /* @__PURE__ */ jsx(Dialog$1.Close, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "outline", children: "Close" }) })
      ]
    }
  );
}
function DialogTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Dialog$1.Title,
    {
      "data-slot": "dialog-title",
      className: cn("text-lg leading-none font-semibold", className),
      ...props
    }
  );
}
function DialogDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Dialog$1.Description,
    {
      "data-slot": "dialog-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function Select({
  ...props
}) {
  return /* @__PURE__ */ jsx(Select$1.Root, { "data-slot": "select", ...props });
}
function SelectGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx(Select$1.Group, { "data-slot": "select-group", ...props });
}
function SelectValue({
  ...props
}) {
  return /* @__PURE__ */ jsx(Select$1.Value, { "data-slot": "select-value", ...props });
}
function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    Select$1.Trigger,
    {
      "data-slot": "select-trigger",
      "data-size": size,
      className: cn(
        "flex w-fit items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[placeholder]:text-muted-foreground data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(Select$1.Icon, { asChild: true, children: /* @__PURE__ */ jsx(ChevronDownIcon, { className: "size-4 opacity-50" }) })
      ]
    }
  );
}
function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}) {
  return /* @__PURE__ */ jsx(Select$1.Portal, { children: /* @__PURE__ */ jsxs(
    Select$1.Content,
    {
      "data-slot": "select-content",
      className: cn(
        "relative z-50 max-h-[var(--radix-select-content-available-height)] min-w-[8rem] origin-[var(--radix-select-content-transform-origin)] overflow-x-hidden overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      ),
      position,
      ...props,
      children: [
        /* @__PURE__ */ jsx(SelectScrollUpButton, {}),
        /* @__PURE__ */ jsx(
          Select$1.Viewport,
          {
            className: cn(
              "p-1",
              position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"
            ),
            children
          }
        ),
        /* @__PURE__ */ jsx(SelectScrollDownButton, {})
      ]
    }
  ) });
}
function SelectLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Select$1.Label,
    {
      "data-slot": "select-label",
      className: cn("px-2 py-1.5 text-xs text-muted-foreground", className),
      ...props
    }
  );
}
function SelectItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    Select$1.Item,
    {
      "data-slot": "select-item",
      className: cn(
        "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none select-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground [&>span:last-child]:flex [&>span:last-child]:items-center [&>span:last-child]:gap-2",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            "data-slot": "select-item-indicator",
            className: "absolute right-2 flex size-3.5 items-center justify-center",
            children: /* @__PURE__ */ jsx(Select$1.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon, { className: "size-4" }) })
          }
        ),
        /* @__PURE__ */ jsx(Select$1.ItemText, { children })
      ]
    }
  );
}
function SelectSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Select$1.Separator,
    {
      "data-slot": "select-separator",
      className: cn("pointer-events-none -mx-1 my-1 h-px bg-border", className),
      ...props
    }
  );
}
function SelectScrollUpButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Select$1.ScrollUpButton,
    {
      "data-slot": "select-scroll-up-button",
      className: cn(
        "flex cursor-default items-center justify-center py-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(ChevronUpIcon, { className: "size-4" })
    }
  );
}
function SelectScrollDownButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Select$1.ScrollDownButton,
    {
      "data-slot": "select-scroll-down-button",
      className: cn(
        "flex cursor-default items-center justify-center py-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(ChevronDownIcon, { className: "size-4" })
    }
  );
}
function Separator({
  className,
  orientation = "horizontal",
  decorative = true,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Separator$1.Root,
    {
      "data-slot": "separator",
      decorative,
      orientation,
      className: cn(
        "shrink-0 bg-border data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px",
        className
      ),
      ...props
    }
  );
}
var Toaster = ({ ...props }) => {
  return /* @__PURE__ */ jsx(
    Toaster$1,
    {
      className: "toaster group",
      style: {
        // AI-UI-195 (FND-004): these tokens are raw HSL TRIPLETS, not colours.
        // styles/globals.css defines e.g. `--popover: 0 0% 100%`, and every
        // other consumer wraps them — the Tailwind preset uses
        // hsl(var(--popover)). Handing sonner `var(--popover)` produced
        // `background: 0 0% 100%`, which is not a valid colour, so the
        // declaration was dropped and toasts silently fell back to sonner's
        // own theme instead of the app's.
        //
        // --border-radius is deliberately NOT wrapped: --radius is a length
        // (0.5rem), not a triplet.
        "--normal-bg": "hsl(var(--popover))",
        "--normal-text": "hsl(var(--popover-foreground))",
        "--normal-border": "hsl(var(--border))",
        "--border-radius": "var(--radius)"
      },
      ...props
    }
  );
};
function Tabs({
  className,
  orientation = "horizontal",
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Tabs$1.Root,
    {
      "data-slot": "tabs",
      "data-orientation": orientation,
      orientation,
      className: cn(
        "group/tabs flex gap-2 data-[orientation=horizontal]:flex-col",
        className
      ),
      ...props
    }
  );
}
var tabsListVariants = cva(
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground group-data-[orientation=horizontal]/tabs:h-9 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col data-[variant=line]:rounded-none",
  {
    variants: {
      variant: {
        default: "bg-muted",
        line: "gap-1 bg-transparent"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function TabsList({
  className,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Tabs$1.List,
    {
      "data-slot": "tabs-list",
      "data-variant": variant,
      className: cn(tabsListVariants({ variant }), className),
      ...props
    }
  );
}
function TabsTrigger({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Tabs$1.Trigger,
    {
      "data-slot": "tabs-trigger",
      className: cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 group-data-[variant=default]/tabs-list:data-[state=active]:shadow-sm group-data-[variant=line]/tabs-list:data-[state=active]:shadow-none dark:text-muted-foreground dark:hover:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:border-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent",
        "data-[state=active]:bg-background data-[state=active]:text-foreground dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 dark:data-[state=active]:text-foreground",
        "after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:bottom-[-5px] group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-[state=active]:after:opacity-100",
        className
      ),
      ...props
    }
  );
}
function TabsContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Tabs$1.Content,
    {
      "data-slot": "tabs-content",
      className: cn("flex-1 outline-none", className),
      ...props
    }
  );
}
function TooltipProvider({
  delayDuration = 0,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Tooltip$1.Provider,
    {
      "data-slot": "tooltip-provider",
      delayDuration,
      ...props
    }
  );
}
function Tooltip9({
  ...props
}) {
  return /* @__PURE__ */ jsx(Tooltip$1.Root, { "data-slot": "tooltip", ...props });
}
function TooltipTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx(Tooltip$1.Trigger, { "data-slot": "tooltip-trigger", ...props });
}
function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(Tooltip$1.Portal, { children: /* @__PURE__ */ jsxs(
    Tooltip$1.Content,
    {
      "data-slot": "tooltip-content",
      sideOffset,
      className: cn(
        "z-50 w-fit origin-[var(--radix-tooltip-content-transform-origin)] animate-in rounded-md bg-foreground px-3 py-1.5 text-xs text-balance text-background fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(Tooltip$1.Arrow, { className: "z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground" })
      ]
    }
  ) });
}
function AppShellV2({
  topBar,
  sideNav,
  sideNavFooter,
  children,
  defaultCollapsed = false,
  sidebarWidthClass = "md:w-60",
  className
}) {
  const [mobileOpen, setMobileOpen] = React5.useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = React5.useState(defaultCollapsed);
  const closeMobile = React5.useCallback(() => setMobileOpen(false), []);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex h-screen flex-col overflow-hidden bg-background", className), children: [
    topBar,
    /* @__PURE__ */ jsxs("div", { className: "flex flex-1 overflow-hidden", children: [
      sideNav && /* @__PURE__ */ jsxs(
        "aside",
        {
          className: cn(
            "hidden md:flex md:flex-col border-r border-border bg-background overflow-y-auto transition-all duration-200",
            desktopCollapsed ? "md:w-0 md:overflow-hidden md:border-r-0" : sidebarWidthClass
          ),
          children: [
            sideNav({ collapsed: desktopCollapsed, onNavigate: closeMobile }),
            sideNavFooter && !desktopCollapsed && /* @__PURE__ */ jsx("div", { className: "mt-auto border-t border-border p-3", children: sideNavFooter })
          ]
        }
      ),
      sideNav && /* @__PURE__ */ jsx("div", { className: "hidden md:flex items-start pt-2", children: /* @__PURE__ */ jsx(
        Button,
        {
          variant: "ghost",
          size: "icon",
          className: "h-7 w-7 rounded-none rounded-r-md border border-l-0 border-border text-skyxops-beach hover:text-skyxops-beach",
          onClick: () => setDesktopCollapsed(!desktopCollapsed),
          "aria-label": desktopCollapsed ? "Expand sidebar" : "Collapse sidebar",
          children: desktopCollapsed ? /* @__PURE__ */ jsx(PanelLeftOpen, { className: "h-4 w-4" }) : /* @__PURE__ */ jsx(PanelLeftClose, { className: "h-4 w-4" })
        }
      ) }),
      sideNav && /* @__PURE__ */ jsxs(Sheet, { open: mobileOpen, onOpenChange: setMobileOpen, children: [
        /* @__PURE__ */ jsx("div", { className: "flex md:hidden items-start pt-2", children: /* @__PURE__ */ jsx(SheetTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
          Button,
          {
            variant: "ghost",
            size: "icon",
            className: "h-7 w-7 rounded-none rounded-r-md border border-l-0 border-border text-skyxops-beach hover:text-skyxops-beach",
            "aria-label": "Open navigation",
            children: /* @__PURE__ */ jsx(Menu, { className: "h-4 w-4" })
          }
        ) }) }),
        /* @__PURE__ */ jsx(SheetContent, { side: "left", className: "w-60 p-0", children: sideNav({ collapsed: false, onNavigate: closeMobile }) })
      ] }),
      /* @__PURE__ */ jsx("main", { className: "flex-1 overflow-y-auto p-4 md:p-6", children })
    ] })
  ] });
}
function TopBar({
  left,
  center,
  right,
  banner,
  style,
  className
}) {
  return /* @__PURE__ */ jsxs(
    "header",
    {
      className: cn(
        "sticky top-0 z-30 flex flex-col border-b border-skyxops-midnight/20 bg-skyxops-midnight text-white",
        className
      ),
      style,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex h-14 items-center px-4", children: [
          /* @__PURE__ */ jsx("div", { className: "flex items-center gap-3", children: left }),
          /* @__PURE__ */ jsx("div", { className: "flex-1 flex justify-center", children: center }),
          /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: right })
        ] }),
        banner
      ]
    }
  );
}
function SideNav({
  sections,
  currentPath,
  onNavigate,
  canAccess,
  collapsed = false,
  className
}) {
  const initialExpanded = React5.useMemo(() => {
    const set = /* @__PURE__ */ new Set();
    for (const s of sections) {
      if (s.links.some((l) => currentPath.startsWith(l.href))) set.add(s.label);
    }
    return set;
  }, [sections, currentPath]);
  const [expanded, setExpanded] = React5.useState(initialExpanded);
  React5.useEffect(() => {
    setExpanded((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const label of initialExpanded) {
        if (!next.has(label)) {
          next.add(label);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [initialExpanded]);
  const toggle = (label) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };
  const visible = React5.useMemo(
    () => sections.map((s) => ({
      ...s,
      links: canAccess ? s.links.filter((l) => canAccess(l.resource)) : s.links
    })).filter((s) => s.links.length > 0),
    [sections, canAccess]
  );
  if (collapsed) return null;
  return /* @__PURE__ */ jsx("nav", { className: cn("flex flex-col gap-1 p-3", className), children: visible.map((section) => {
    const isExpanded = expanded.has(section.label);
    const Icon = section.icon;
    const hasActive = section.links.some(
      (l) => currentPath === l.href || currentPath.startsWith(l.href + "/")
    );
    return /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: () => toggle(section.label),
          className: cn(
            "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            hasActive ? "text-skyxops-lilac" : "text-foreground"
          ),
          "aria-expanded": isExpanded,
          children: [
            /* @__PURE__ */ jsx(
              Icon,
              {
                className: cn("h-4 w-4 shrink-0", hasActive && !section.iconColor && "text-skyxops-lilac"),
                style: section.iconColor ? { color: section.iconColor } : void 0
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "flex-1 text-left", children: section.label }),
            isExpanded ? /* @__PURE__ */ jsx(ChevronDown, { className: "h-3 w-3" }) : /* @__PURE__ */ jsx(ChevronRight, { className: "h-3 w-3" })
          ]
        }
      ),
      isExpanded && /* @__PURE__ */ jsx("div", { className: "ml-4 mt-1 flex flex-col gap-0.5 border-l border-border pl-3", children: section.links.map((link) => {
        const isActive = currentPath === link.href;
        return /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: () => onNavigate(link.href),
            className: cn(
              "rounded-md px-3 py-1.5 text-left text-sm transition-colors",
              isActive ? "bg-skyxops-lilac/10 font-semibold text-skyxops-lilac border-l-2 border-skyxops-lilac -ml-px" : "text-foreground/80 hover:bg-accent hover:text-foreground"
            ),
            children: link.label
          },
          link.href
        );
      }) })
    ] }, section.label);
  }) });
}
function SplitPanel({
  master,
  detail,
  defaultWidth = 420,
  minWidth = 280,
  maxWidth = 720,
  defaultCollapsed = false,
  className
}) {
  const [collapsed, setCollapsed] = React5.useState(defaultCollapsed);
  const [maximized, setMaximized] = React5.useState(false);
  const [width, setWidth] = React5.useState(defaultWidth);
  const dragRef = React5.useRef(null);
  const detachDragRef = React5.useRef(null);
  React5.useEffect(() => () => detachDragRef.current?.(), []);
  const onResizeStart = React5.useCallback(
    (e) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: width };
      const onMove = (mv) => {
        if (!dragRef.current) return;
        const delta = dragRef.current.startX - mv.clientX;
        const next = Math.min(maxWidth, Math.max(minWidth, dragRef.current.startWidth + delta));
        setWidth(next);
      };
      const detach = () => {
        dragRef.current = null;
        detachDragRef.current = null;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      const onUp = () => detach();
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      detachDragRef.current = detach;
    },
    [width, minWidth, maxWidth]
  );
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-1 min-h-0 border rounded-lg overflow-hidden", className), children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "flex flex-col min-w-0 overflow-hidden",
          maximized ? "w-1/4 shrink-0" : "flex-1"
        ),
        children: master
      }
    ),
    !collapsed && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "w-1 cursor-col-resize bg-border hover:bg-skyxops-lilac/50 active:bg-skyxops-lilac transition-colors shrink-0",
          onMouseDown: onResizeStart,
          title: "Drag to resize"
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: cn("bg-background flex flex-col min-h-0 overflow-hidden", maximized ? "flex-1" : "shrink-0"),
          style: maximized ? void 0 : { width },
          children: detail({ collapsed, setCollapsed, maximized, setMaximized })
        }
      )
    ] }),
    collapsed && detail({ collapsed, setCollapsed, maximized, setMaximized })
  ] });
}
function BuildStamp({
  value = "dev",
  prefix = "Build:",
  className
}) {
  return /* @__PURE__ */ jsxs("p", { className: cn("text-xs text-muted-foreground", className), children: [
    prefix,
    " ",
    value
  ] });
}
function KpiGridSkeleton({
  count = 3,
  cardHeight = "h-24",
  className
}) {
  return /* @__PURE__ */ jsx("div", { className: cn("grid grid-cols-1 gap-4 md:grid-cols-3", className), children: Array.from({ length: count }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: cardHeight }, i)) });
}
function SavedFilterChips({
  filters,
  onApply,
  onDelete,
  deleteAriaLabel = "Delete saved filter",
  className
}) {
  if (filters.length === 0) return null;
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap items-center gap-2", className), children: filters.map((f) => /* @__PURE__ */ jsxs(
    Badge,
    {
      variant: "outline",
      className: "gap-1 pr-1 text-xs font-normal",
      children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "hover:underline",
            onClick: () => onApply(f.id),
            children: f.name
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground",
            onClick: () => onDelete(f.id),
            "aria-label": deleteAriaLabel,
            children: /* @__PURE__ */ jsx(X, { className: "h-3 w-3" })
          }
        )
      ]
    },
    String(f.id)
  )) });
}
function SectionCard({
  title,
  description,
  action,
  variant,
  children,
  className,
  contentClassName,
  collapsible,
  defaultCollapsed
}) {
  const [open, setOpen] = React5.useState(!(collapsible && defaultCollapsed));
  const titleNode = /* @__PURE__ */ jsx(CardTitle, { className: "font-display text-base font-semibold tracking-tight", children: title });
  return /* @__PURE__ */ jsxs(Card, { className: cn(variant === "elevated" && ELEVATED_CARD_CLASSNAME, className), children: [
    /* @__PURE__ */ jsxs(CardHeader, { className: cn((action || collapsible) && "flex-row items-start justify-between gap-3 space-y-0"), children: [
      /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-1.5", children: [
        collapsible ? /* @__PURE__ */ jsxs("button", { type: "button", onClick: () => setOpen((v) => !v), className: "flex items-center gap-1.5 text-left", "aria-expanded": open, children: [
          titleNode,
          /* @__PURE__ */ jsx(ChevronDown, { className: cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180") })
        ] }) : titleNode,
        description && /* @__PURE__ */ jsx(CardDescription, { children: description })
      ] }),
      action && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: action })
    ] }),
    (!collapsible || open) && /* @__PURE__ */ jsx(CardContent, { className: contentClassName, children })
  ] });
}
var CONFIG_SECTION_TONES = {
  neutral: { accent: "border-l-border", header: "bg-muted", title: "text-foreground" },
  lilac: { accent: "border-l-skyxops-lilac", header: "bg-skyxops-lilac/10", title: "text-skyxops-lilac" },
  beach: { accent: "border-l-skyxops-beach", header: "bg-skyxops-beach/10", title: "text-skyxops-beach" },
  orange: { accent: "border-l-skyxops-orange", header: "bg-skyxops-orange/10", title: "text-skyxops-orange" },
  midnight: {
    accent: "border-l-skyxops-midnight",
    header: "bg-skyxops-midnight/5",
    title: "text-skyxops-midnight dark:text-skyxops-mist"
  }
};
function HeaderInner({
  icon,
  title,
  toneClasses
}) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    icon && /* @__PURE__ */ jsx("span", { className: cn("shrink-0", toneClasses.title), children: icon }),
    /* @__PURE__ */ jsx("span", { className: cn("truncate text-sm font-semibold", toneClasses.title), children: title })
  ] });
}
function ConfigSection({
  title,
  icon,
  action,
  tone = "neutral",
  collapsible = true,
  defaultOpen = true,
  open,
  onOpenChange,
  children,
  className,
  contentClassName
}) {
  const toneClasses = CONFIG_SECTION_TONES[tone];
  const headerBar = cn(
    "flex items-center gap-2 border-l-2 px-3 py-2",
    toneClasses.accent,
    toneClasses.header
  );
  const body = /* @__PURE__ */ jsx("div", { className: cn("px-3 py-3", contentClassName), children });
  const wrapper = cn("overflow-hidden rounded-lg border bg-card", className);
  if (!collapsible) {
    return /* @__PURE__ */ jsxs("div", { className: wrapper, children: [
      /* @__PURE__ */ jsxs("div", { className: headerBar, children: [
        /* @__PURE__ */ jsx("div", { className: "flex min-w-0 flex-1 items-center gap-2", children: /* @__PURE__ */ jsx(HeaderInner, { icon, title, toneClasses }) }),
        action && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: action })
      ] }),
      body
    ] });
  }
  return /* @__PURE__ */ jsxs(
    Collapsible,
    {
      open,
      defaultOpen: open === void 0 ? defaultOpen : void 0,
      onOpenChange,
      className: wrapper,
      children: [
        /* @__PURE__ */ jsxs("div", { className: headerBar, children: [
          /* @__PURE__ */ jsxs(
            CollapsibleTrigger,
            {
              className: "group flex min-w-0 flex-1 items-center gap-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              children: [
                /* @__PURE__ */ jsx(
                  ChevronDown,
                  {
                    "aria-hidden": true,
                    className: cn(
                      "h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=closed]:-rotate-90",
                      toneClasses.title
                    )
                  }
                ),
                /* @__PURE__ */ jsx(HeaderInner, { icon, title, toneClasses })
              ]
            }
          ),
          action && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: action })
        ] }),
        /* @__PURE__ */ jsx(CollapsibleContent, { children: body })
      ]
    }
  );
}
var ALL_FILTER_VALUE = "__all__";
function unwrapFilterValue(v) {
  return v === ALL_FILTER_VALUE ? "" : v;
}
function normalize(opt) {
  return typeof opt === "string" ? { value: opt, label: opt } : opt;
}
function FilterSelect({
  label,
  value,
  onChange,
  options,
  triggerClassName,
  allLabel = "All",
  allValue = ALL_FILTER_VALUE,
  includeAll = true,
  className
}) {
  return /* @__PURE__ */ jsxs(Select, { value, onValueChange: onChange, children: [
    /* @__PURE__ */ jsxs(
      SelectTrigger,
      {
        className: cn("gap-1.5 overflow-hidden", triggerClassName, className),
        "aria-label": typeof label === "string" ? label : void 0,
        children: [
          /* @__PURE__ */ jsxs("span", { className: "shrink-0 text-muted-foreground", children: [
            label,
            ":"
          ] }),
          /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1 truncate text-left", children: /* @__PURE__ */ jsx(SelectValue, {}) })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(SelectContent, { children: [
      includeAll && /* @__PURE__ */ jsx(SelectItem, { value: allValue, children: allLabel }),
      options.map(normalize).filter((o) => o.value !== "").map((o) => /* @__PURE__ */ jsx(SelectItem, { value: o.value, children: o.label }, o.value))
    ] })
  ] });
}
function FormField({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  className
}) {
  const Wrapper = htmlFor ? "div" : "label";
  const labelEl = /* @__PURE__ */ jsxs("span", { className: "text-xs uppercase tracking-wider text-muted-foreground", children: [
    label,
    required && /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "ml-0.5 text-destructive", children: "*" })
  ] });
  return /* @__PURE__ */ jsxs(Wrapper, { className: cn("block space-y-1", className), children: [
    htmlFor ? /* @__PURE__ */ jsx("label", { htmlFor, className: "cursor-pointer", children: labelEl }) : labelEl,
    /* @__PURE__ */ jsx("div", { children }),
    error ? /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", role: "alert", children: error }) : hint ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: hint }) : null
  ] });
}
var _formIdCounter = 0;
function nextFormId() {
  _formIdCounter += 1;
  return `skyxops-form-dialog-${_formIdCounter}`;
}
function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  onSubmit,
  submitLabel,
  cancelLabel = "Cancel",
  busyLabel,
  busy = false,
  submitDisabled = false,
  errorTitle = "Couldn't save",
  error,
  contentClassName,
  formId
}) {
  const id = React5.useMemo(() => formId ?? nextFormId(), [formId]);
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(DialogContent, { className: contentClassName, children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsx(DialogTitle, { children: title }),
      description && /* @__PURE__ */ jsx(DialogDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsxs("form", { id, onSubmit, className: cn("space-y-4"), children: [
      children,
      error && /* @__PURE__ */ jsxs(Alert, { variant: "destructive", children: [
        /* @__PURE__ */ jsx(AlertTitle, { children: errorTitle }),
        /* @__PURE__ */ jsx(AlertDescription, { children: error })
      ] })
    ] }),
    /* @__PURE__ */ jsxs(DialogFooter, { children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          variant: "outline",
          onClick: () => onOpenChange(false),
          disabled: busy,
          children: cancelLabel
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "submit",
          form: id,
          disabled: busy || submitDisabled,
          children: busy && busyLabel ? busyLabel : submitLabel
        }
      )
    ] })
  ] }) });
}
function defaultLauncherUrl() {
  if (typeof window === "undefined") return "http://localhost:8000/";
  const { protocol, host } = window.location;
  if (host.startsWith("localhost") || host.startsWith("127.0.0.1")) {
    return "http://localhost:8000/";
  }
  const labels = host.split(".");
  labels[0] = "launchpad";
  return `${protocol}//${labels.join(".")}/`;
}
function BackToLauncherButton({
  launcherUrl,
  children = "\u2190 Launch Pad",
  className
}) {
  const target = launcherUrl ?? defaultLauncherUrl();
  return /* @__PURE__ */ jsxs(
    Button,
    {
      variant: "ghost",
      size: "sm",
      className: cn("text-white/80 hover:bg-white/10 hover:text-white", className),
      onClick: () => {
        window.location.href = target;
      },
      children: [
        /* @__PURE__ */ jsx(ExternalLink, { className: "mr-2 h-4 w-4" }),
        children
      ]
    }
  );
}
var SPARK = [10, 12, 11, 14, 13, 16, 15, 17, 19, 18, 21, 22];
var TREND = SPARK.map((v, i) => ({ label: `M${i + 1}`, value: v * 100 }));
var TOP_SERVICES = [
  { label: "EC2", value: 12500 },
  { label: "S3", value: 4800 },
  { label: "RDS", value: 3200 },
  { label: "Lambda", value: 1100 },
  { label: "CloudFront", value: 600 }
];
var PIE_DATA = [
  { label: "AWS", value: 65 },
  { label: "Azure", value: 22 },
  { label: "GCP", value: 13 }
];
var STACKED = [
  { label: "Jan", values: { ec2: 100, s3: 50, rds: 30 } },
  { label: "Feb", values: { ec2: 120, s3: 55, rds: 32 } },
  { label: "Mar", values: { ec2: 90, s3: 60, rds: 35 } },
  { label: "Apr", values: { ec2: 130, s3: 58, rds: 40 } }
];
var STACKED_SERIES = [
  { key: "ec2", name: "EC2" },
  { key: "s3", name: "S3" },
  { key: "rds", name: "RDS" }
];
var MULTI = [
  { label: "Jan", values: { prod: 1e3, dev: 200, qa: 150 } },
  { label: "Feb", values: { prod: 1200, dev: 220, qa: 180 } },
  { label: "Mar", values: { prod: 1100, dev: 210, qa: 170 } },
  { label: "Apr", values: { prod: 1400, dev: 230, qa: 190 } }
];
var MULTI_SERIES = [
  { key: "prod", name: "Production" },
  { key: "dev", name: "Development" },
  { key: "qa", name: "QA" }
];
var SANKEY_NODES = [
  { id: "aws", name: "AWS" },
  { id: "azure", name: "Azure" },
  { id: "compute", name: "Compute" },
  { id: "storage", name: "Storage" },
  { id: "database", name: "Database" },
  { id: "prod", name: "Prod" },
  { id: "dev", name: "Dev" }
];
var SANKEY_LINKS = [
  { source: "aws", target: "compute", value: 4200 },
  { source: "aws", target: "storage", value: 1800 },
  { source: "azure", target: "compute", value: 1200 },
  { source: "azure", target: "database", value: 900 },
  { source: "compute", target: "prod", value: 4800 },
  { source: "compute", target: "dev", value: 600 },
  { source: "storage", target: "prod", value: 1500 },
  { source: "storage", target: "dev", value: 300 },
  { source: "database", target: "prod", value: 700 },
  { source: "database", target: "dev", value: 200 }
];
function Section({
  title,
  description,
  children
}) {
  return /* @__PURE__ */ jsxs("section", { className: "space-y-3 rounded-lg border border-border bg-card p-5", children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold", children: title }),
      description && /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: description })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "space-y-3", children })
  ] });
}
function Cell4({ label, children }) {
  return /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
    /* @__PURE__ */ jsx("div", { className: "text-xs uppercase tracking-wider text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx("div", { children })
  ] });
}
function Gallery() {
  const [pageIdx, setPageIdx] = useState(1);
  const [select, setSelect] = useState("aws");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [filterProvider, setFilterProvider] = useState(ALL_FILTER_VALUE);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formName, setFormName] = useState("");
  return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsxs("div", { className: "mx-auto max-w-6xl space-y-6 p-6", children: [
    /* @__PURE__ */ jsxs("header", { children: [
      /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold", children: "@skyxops/ui \u2014 Gallery" }),
      /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Live catalog of every primitive in the current theme. Toggle dark mode (sun/moon in the TopBar) to see the dark variants." })
    ] }),
    /* @__PURE__ */ jsx(Section, { title: "Formatters", description: "Money, DateTime, NumberDisplay + format helpers (per swa #52/53/56)", children: /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-4 md:grid-cols-3", children: [
      /* @__PURE__ */ jsxs(Cell4, { label: "Money", children: [
        /* @__PURE__ */ jsx(Money, { value: 12345.67 }),
        /* @__PURE__ */ jsxs("div", { className: "text-xs text-muted-foreground", children: [
          "formatMoney: ",
          formatMoney(12345.67)
        ] })
      ] }),
      /* @__PURE__ */ jsxs(Cell4, { label: "DateTime (full)", children: [
        /* @__PURE__ */ jsx(DateTime, { value: "2026-05-05T18:42:11Z" }),
        /* @__PURE__ */ jsxs("div", { className: "text-xs text-muted-foreground", children: [
          "formatDateTime (date): ",
          formatDateTime("2026-05-05T18:42:11Z", { format: "date" })
        ] })
      ] }),
      /* @__PURE__ */ jsxs(Cell4, { label: "NumberDisplay", children: [
        /* @__PURE__ */ jsx(NumberDisplay, { value: 123456789e-2, maximumFractionDigits: 0 }),
        /* @__PURE__ */ jsxs("div", { className: "text-xs text-muted-foreground", children: [
          "formatNumber: ",
          formatNumber(0.4231, { style: "percent", maximumFractionDigits: 1 })
        ] })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(Section, { title: "KpiCard + TrendIndicator", children: /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-4 md:grid-cols-3", children: [
      /* @__PURE__ */ jsx(KpiCard, { label: "Month-to-date", value: "$12,346.78", sub: "423 usage rows" }),
      /* @__PURE__ */ jsx(KpiCard, { label: "Active resources", value: "1,287", sub: "across 3 clouds" }),
      /* @__PURE__ */ jsx(
        KpiCard,
        {
          label: "MTD trend",
          value: /* @__PURE__ */ jsx(TrendIndicator, { data: SPARK, delta: "+9%", deltaSign: 1 }),
          sub: "vs. last month"
        }
      )
    ] }) }),
    /* @__PURE__ */ jsxs(Section, { title: "Charts (Recharts-based)", children: [
      /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-4 md:grid-cols-2", children: [
        /* @__PURE__ */ jsx(Cell4, { label: "LineChart", children: /* @__PURE__ */ jsx(LineChart, { data: TREND, height: 180, smooth: true }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "AreaChart", children: /* @__PURE__ */ jsx(AreaChart, { data: TREND, height: 180 }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "BarChart", children: /* @__PURE__ */ jsx(BarChart, { data: TOP_SERVICES, height: 180 }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "HorizontalBarChart", children: /* @__PURE__ */ jsx(HorizontalBarChart, { data: TOP_SERVICES, height: 180, labelWidth: 120 }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "PieChart", children: /* @__PURE__ */ jsx(PieChart, { data: PIE_DATA, height: 180 }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "GaugeChart", children: /* @__PURE__ */ jsx(
          GaugeChart,
          {
            value: 72,
            max: 100,
            centerLabel: /* @__PURE__ */ jsx(NumberDisplay, { value: 72, unit: "%" }),
            subLabel: "of 100",
            height: 180
          }
        ) }),
        /* @__PURE__ */ jsx(Cell4, { label: "StackedBarChart", children: /* @__PURE__ */ jsx(StackedBarChart, { data: STACKED, series: STACKED_SERIES, height: 180 }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "MultiSeriesLineChart", children: /* @__PURE__ */ jsx(MultiSeriesLineChart, { data: MULTI, series: MULTI_SERIES, height: 180 }) })
      ] }),
      /* @__PURE__ */ jsx(Cell4, { label: "SankeyChart", children: /* @__PURE__ */ jsx(SankeyChart, { nodes: SANKEY_NODES, links: SANKEY_LINKS, height: 260 }) })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "shadcn primitives", description: "Built on Radix headless + class-variance-authority + cn()", children: [
      /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-6 md:grid-cols-2", children: [
        /* @__PURE__ */ jsx(Cell4, { label: "Button variants", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
          /* @__PURE__ */ jsx(Button, { children: "default" }),
          /* @__PURE__ */ jsx(Button, { variant: "secondary", children: "secondary" }),
          /* @__PURE__ */ jsx(Button, { variant: "outline", children: "outline" }),
          /* @__PURE__ */ jsx(Button, { variant: "ghost", children: "ghost" }),
          /* @__PURE__ */ jsx(Button, { variant: "destructive", children: "destructive" }),
          /* @__PURE__ */ jsx(Button, { variant: "link", children: "link" })
        ] }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "Badge variants", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
          /* @__PURE__ */ jsx(Badge, { children: "default" }),
          /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "secondary" }),
          /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "outline" }),
          /* @__PURE__ */ jsx(Badge, { variant: "destructive", children: "destructive" })
        ] }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "Alert (info / warning / destructive)", children: /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsxs(Alert, { children: [
            /* @__PURE__ */ jsx(Info, { className: "h-4 w-4" }),
            /* @__PURE__ */ jsx(AlertTitle, { children: "Info" }),
            /* @__PURE__ */ jsx(AlertDescription, { children: "Default alert tone." })
          ] }),
          /* @__PURE__ */ jsxs(Alert, { variant: "destructive", children: [
            /* @__PURE__ */ jsx(TriangleAlert, { className: "h-4 w-4" }),
            /* @__PURE__ */ jsx(AlertTitle, { children: "Couldn't load" }),
            /* @__PURE__ */ jsx(AlertDescription, { children: "Destructive variant." })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "Input", children: /* @__PURE__ */ jsx(Input, { placeholder: "Type something\u2026" }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "Select (with __all__ sentinel)", children: /* @__PURE__ */ jsxs(Select, { value: select, onValueChange: setSelect, children: [
          /* @__PURE__ */ jsx(SelectTrigger, { className: "w-full", children: /* @__PURE__ */ jsx(SelectValue, {}) }),
          /* @__PURE__ */ jsxs(SelectContent, { children: [
            /* @__PURE__ */ jsx(SelectItem, { value: "aws", children: "AWS" }),
            /* @__PURE__ */ jsx(SelectItem, { value: "azure", children: "Azure" }),
            /* @__PURE__ */ jsx(SelectItem, { value: "gcp", children: "GCP" })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "Skeleton (loading placeholder)", children: /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-1/2" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-2/3" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-1/3" })
        ] }) }),
        /* @__PURE__ */ jsxs(Cell4, { label: "Separator", children: [
          /* @__PURE__ */ jsx("div", { children: "Above" }),
          /* @__PURE__ */ jsx(Separator, { className: "my-2" }),
          /* @__PURE__ */ jsx("div", { children: "Below" })
        ] }),
        /* @__PURE__ */ jsx(Cell4, { label: "Tooltip", children: /* @__PURE__ */ jsxs(Tooltip9, { children: [
          /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "outline", children: "Hover me" }) }),
          /* @__PURE__ */ jsx(TooltipContent, { children: "This is a tooltip" })
        ] }) })
      ] }),
      /* @__PURE__ */ jsx(Cell4, { label: "Card", children: /* @__PURE__ */ jsxs(Card, { className: "max-w-md", children: [
        /* @__PURE__ */ jsxs(CardHeader, { children: [
          /* @__PURE__ */ jsx(CardTitle, { children: "Card title" }),
          /* @__PURE__ */ jsx(CardDescription, { children: "One-line description." })
        ] }),
        /* @__PURE__ */ jsx(CardContent, { children: "Content body sits here." }),
        /* @__PURE__ */ jsx(CardFooter, { children: /* @__PURE__ */ jsx(Button, { size: "sm", children: "Footer action" }) })
      ] }) }),
      /* @__PURE__ */ jsx(Cell4, { label: "Tabs", children: /* @__PURE__ */ jsxs(Tabs, { defaultValue: "overview", className: "max-w-md", children: [
        /* @__PURE__ */ jsxs(TabsList, { children: [
          /* @__PURE__ */ jsx(TabsTrigger, { value: "overview", children: "Overview" }),
          /* @__PURE__ */ jsx(TabsTrigger, { value: "details", children: "Details" })
        ] }),
        /* @__PURE__ */ jsx(TabsContent, { value: "overview", children: "Overview tab content." }),
        /* @__PURE__ */ jsx(TabsContent, { value: "details", children: "Details tab content." })
      ] }) }),
      /* @__PURE__ */ jsx(Cell4, { label: "Collapsible", children: /* @__PURE__ */ jsxs(Collapsible, { className: "max-w-md", children: [
        /* @__PURE__ */ jsx(CollapsibleTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", children: "Toggle section" }) }),
        /* @__PURE__ */ jsx(CollapsibleContent, { className: "pt-2 text-sm", children: "Hidden content revealed on click." })
      ] }) }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
        /* @__PURE__ */ jsx(Cell4, { label: "Dialog", children: /* @__PURE__ */ jsxs(Dialog, { children: [
          /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "outline", children: "Open Dialog" }) }),
          /* @__PURE__ */ jsxs(DialogContent, { children: [
            /* @__PURE__ */ jsxs(DialogHeader, { children: [
              /* @__PURE__ */ jsx(DialogTitle, { children: "Sample Dialog" }),
              /* @__PURE__ */ jsx(DialogDescription, { children: "One-line description." })
            ] }),
            /* @__PURE__ */ jsx("div", { className: "text-sm", children: "Dialog body." }),
            /* @__PURE__ */ jsxs(DialogFooter, { children: [
              /* @__PURE__ */ jsx(Button, { variant: "outline", children: "Cancel" }),
              /* @__PURE__ */ jsx(Button, { children: "Confirm" })
            ] })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "DropdownMenu", children: /* @__PURE__ */ jsxs(DropdownMenu, { children: [
          /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "outline", children: "Menu" }) }),
          /* @__PURE__ */ jsxs(DropdownMenuContent, { children: [
            /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Options" }),
            /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
            /* @__PURE__ */ jsx(DropdownMenuItem, { children: "Item 1" }),
            /* @__PURE__ */ jsx(DropdownMenuItem, { children: "Item 2" })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "Sheet", children: /* @__PURE__ */ jsxs(Sheet, { open: sheetOpen, onOpenChange: setSheetOpen, children: [
          /* @__PURE__ */ jsx(SheetTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "outline", children: "Open Sheet" }) }),
          /* @__PURE__ */ jsxs(SheetContent, { children: [
            /* @__PURE__ */ jsxs(SheetHeader, { children: [
              /* @__PURE__ */ jsx(SheetTitle, { children: "Sample Sheet" }),
              /* @__PURE__ */ jsx(SheetDescription, { children: "Slide-out panel." })
            ] }),
            /* @__PURE__ */ jsx("div", { className: "mt-4 text-sm", children: "Sheet body content." })
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(Cell4, { label: "Toast (sonner)", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "outline",
              size: "sm",
              onClick: () => toast.success("Saved successfully", { icon: /* @__PURE__ */ jsx(CheckCircle2, { className: "h-4 w-4" }) }),
              children: "success"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "outline",
              size: "sm",
              onClick: () => toast.error("Couldn\u2019t save", { icon: /* @__PURE__ */ jsx(AlertCircle, { className: "h-4 w-4" }) }),
              children: "error"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "outline",
              size: "sm",
              onClick: () => toast.info("Heads up", { icon: /* @__PURE__ */ jsx(Info, { className: "h-4 w-4" }) }),
              children: "info"
            }
          )
        ] }) })
      ] }),
      /* @__PURE__ */ jsx(Cell4, { label: "Table", children: /* @__PURE__ */ jsxs(Table, { children: [
        /* @__PURE__ */ jsx(TableHeader, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsx(TableHead, { children: "Service" }),
          /* @__PURE__ */ jsx(TableHead, { children: "Provider" }),
          /* @__PURE__ */ jsx(TableHead, { className: "text-right", children: "Spend" })
        ] }) }),
        /* @__PURE__ */ jsx(TableBody, { children: TOP_SERVICES.slice(0, 3).map((s) => /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsx(TableCell, { children: s.label }),
          /* @__PURE__ */ jsx(TableCell, { children: "AWS" }),
          /* @__PURE__ */ jsx(TableCell, { className: "text-right font-mono", children: formatMoney(s.value) })
        ] }, s.label)) })
      ] }) })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "v0.16 Tier-1 promotions", description: "EmptyState (back from the dead), TableLoadingSkeleton, KpiGridSkeleton, SavedFilterChips, SectionCard", children: [
      /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-4 md:grid-cols-2", children: [
        /* @__PURE__ */ jsx(Cell4, { label: "EmptyState", children: /* @__PURE__ */ jsx(
          EmptyState,
          {
            heading: "No findings match these filters.",
            message: "Try clearing them or saving the current set.",
            action: /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", children: "Clear filters" })
          }
        ) }),
        /* @__PURE__ */ jsx(Cell4, { label: "SavedFilterChips", children: /* @__PURE__ */ jsx(
          SavedFilterChips,
          {
            filters: [
              { id: 1, name: "Top services" },
              { id: 2, name: "EC2 only" },
              { id: 3, name: "High-cost regions" }
            ],
            onApply: () => void 0,
            onDelete: () => void 0
          }
        ) })
      ] }),
      /* @__PURE__ */ jsx(Cell4, { label: "KpiGridSkeleton", children: /* @__PURE__ */ jsx(KpiGridSkeleton, { count: 3 }) }),
      /* @__PURE__ */ jsx(Cell4, { label: "TableLoadingSkeleton (3\xD74)", children: /* @__PURE__ */ jsxs(Table, { children: [
        /* @__PURE__ */ jsx(TableHeader, { children: /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsx(TableHead, { children: "Service" }),
          /* @__PURE__ */ jsx(TableHead, { children: "Provider" }),
          /* @__PURE__ */ jsx(TableHead, { children: "Region" }),
          /* @__PURE__ */ jsx(TableHead, { className: "text-right", children: "Spend" })
        ] }) }),
        /* @__PURE__ */ jsx(TableBody, { children: /* @__PURE__ */ jsx(TableLoadingSkeleton, { rows: 3, cols: 4 }) })
      ] }) }),
      /* @__PURE__ */ jsx(Cell4, { label: "SectionCard", children: /* @__PURE__ */ jsx(SectionCard, { title: "12-month trend", description: "Aggregated effective cost", children: /* @__PURE__ */ jsx(LineChart, { data: TREND, height: 140, smooth: true }) }) })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "v0.17 Tier-1/2 promotions", description: "FilterSelect (Tier-2 #6), FormField + FormDialog (Tier-1 #5), BackToLauncherButton (Tier-2 #7)", children: [
      /* @__PURE__ */ jsxs(Cell4, { label: "FilterSelect (with __all__ sentinel)", children: [
        /* @__PURE__ */ jsx(
          FilterSelect,
          {
            label: "Provider",
            value: filterProvider,
            onChange: setFilterProvider,
            options: ["aws", "azure", "gcp"]
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "text-xs text-muted-foreground", children: [
          "Selected (raw): ",
          /* @__PURE__ */ jsx("code", { children: filterProvider })
        ] })
      ] }),
      /* @__PURE__ */ jsx(Cell4, { label: "FormField (label + Input + error)", children: /* @__PURE__ */ jsxs("div", { className: "max-w-md space-y-3", children: [
        /* @__PURE__ */ jsx(FormField, { label: "Name", required: true, hint: "Up to 200 characters", children: /* @__PURE__ */ jsx(Input, { placeholder: "Acme Subsidiary" }) }),
        /* @__PURE__ */ jsx(FormField, { label: "Email", error: "Email is invalid", children: /* @__PURE__ */ jsx(Input, { type: "email", defaultValue: "not an email" }) })
      ] }) }),
      /* @__PURE__ */ jsxs(Cell4, { label: "FormDialog (open below \u2014 click to demo)", children: [
        /* @__PURE__ */ jsx(Button, { variant: "outline", onClick: () => setFormDialogOpen(true), children: "Open FormDialog demo" }),
        /* @__PURE__ */ jsx(
          FormDialog,
          {
            open: formDialogOpen,
            onOpenChange: setFormDialogOpen,
            title: "New client",
            description: "Demo dialog wired to FormField rows.",
            onSubmit: (e) => {
              e.preventDefault();
              setFormDialogOpen(false);
            },
            submitLabel: "Create client",
            busyLabel: "Saving\u2026",
            submitDisabled: !formName.trim(),
            children: /* @__PURE__ */ jsx(FormField, { label: "Name", required: true, children: /* @__PURE__ */ jsx(Input, { value: formName, onChange: (e) => setFormName(e.target.value) }) })
          }
        )
      ] }),
      /* @__PURE__ */ jsx(Cell4, { label: "BackToLauncherButton (rendered against a dark surface for the white-on-dark default)", children: /* @__PURE__ */ jsx("div", { className: "rounded bg-skyxops-midnight p-3", children: /* @__PURE__ */ jsx(BackToLauncherButton, { launcherUrl: "#", children: "\u2190 Launcher" }) }) })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "JobStatusBadge + Pagination", children: [
      /* @__PURE__ */ jsx(Cell4, { label: "JobStatusBadge (each status)", children: /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx(
          JobStatusBadge,
          {
            info: { status: "pending", attempts: 0, max_attempts: 3 },
            onDismiss: () => void 0
          }
        ),
        /* @__PURE__ */ jsx(
          JobStatusBadge,
          {
            info: { status: "running", attempts: 1, max_attempts: 3 },
            onDismiss: () => void 0
          }
        ),
        /* @__PURE__ */ jsx(
          JobStatusBadge,
          {
            info: {
              status: "succeeded",
              attempts: 1,
              max_attempts: 3,
              download_url: "#",
              filename: "export.csv",
              row_count: 423
            },
            onDismiss: () => void 0
          }
        ),
        /* @__PURE__ */ jsx(
          JobStatusBadge,
          {
            info: {
              status: "failed",
              attempts: 3,
              max_attempts: 3,
              error_message: "Upstream API returned 500"
            },
            onDismiss: () => void 0
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx(Cell4, { label: "Pagination", children: /* @__PURE__ */ jsx(
        Pagination,
        {
          page: pageIdx,
          totalPages: 10,
          onPageChange: setPageIdx,
          summary: `Page ${pageIdx} of 10 \xB7 250 rows`
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx(
      Section,
      {
        title: "ConfigSection (AI-UI-156)",
        description: "Color-separated, collapsible section headers for config surfaces (the Insights Configure Split Inspector). Tones from CONFIG_SECTION_TONES (sgg \xA79).",
        children: /* @__PURE__ */ jsxs("div", { className: "max-w-md space-y-2", children: [
          /* @__PURE__ */ jsx(ConfigSection, { title: "Dataset", tone: "midnight", icon: /* @__PURE__ */ jsx(Database, { className: "h-4 w-4" }), collapsible: false, children: /* @__PURE__ */ jsx("div", { className: "text-sm text-muted-foreground", children: "Cloud Cost & Spend" }) }),
          /* @__PURE__ */ jsx(
            ConfigSection,
            {
              title: "Filters",
              tone: "lilac",
              icon: /* @__PURE__ */ jsx(Filter, { className: "h-4 w-4" }),
              action: /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "3 active" }),
              children: /* @__PURE__ */ jsx("div", { className: "text-sm text-muted-foreground", children: "Provider \xB7 Service \xB7 Region pickers go here." })
            }
          ),
          /* @__PURE__ */ jsx(
            ConfigSection,
            {
              title: "Break down by",
              tone: "beach",
              icon: /* @__PURE__ */ jsx(Layers3, { className: "h-4 w-4" }),
              defaultOpen: false,
              children: /* @__PURE__ */ jsx("div", { className: "text-sm text-muted-foreground", children: "Dimension levels go here." })
            }
          )
        ] })
      }
    )
  ] }) });
}
var ConfirmContext = React5.createContext(null);
function ConfirmProvider({ children }) {
  const [pending, setPending] = React5.useState(null);
  const pendingRef = React5.useRef(null);
  const confirm = React5.useCallback((options) => {
    return new Promise((resolve) => {
      pendingRef.current?.resolve(false);
      const next = { ...options, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);
  const settle = React5.useCallback(
    (value) => {
      const current = pendingRef.current;
      pendingRef.current = null;
      setPending(null);
      current?.resolve(value);
    },
    []
  );
  React5.useEffect(
    () => () => {
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    },
    []
  );
  return /* @__PURE__ */ jsxs(ConfirmContext.Provider, { value: confirm, children: [
    children,
    /* @__PURE__ */ jsx(
      Dialog,
      {
        open: pending !== null,
        onOpenChange: (open) => {
          if (!open) settle(false);
        },
        children: pending && /* @__PURE__ */ jsxs(DialogContent, { className: "sm:max-w-md", children: [
          /* @__PURE__ */ jsxs(DialogHeader, { children: [
            /* @__PURE__ */ jsx(DialogTitle, { children: pending.title }),
            pending.description && /* @__PURE__ */ jsx(DialogDescription, { children: pending.description })
          ] }),
          /* @__PURE__ */ jsxs(DialogFooter, { children: [
            /* @__PURE__ */ jsx(Button, { variant: "outline", onClick: () => settle(false), children: pending.cancelLabel ?? "Cancel" }),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: pending.destructive ? "destructive" : "default",
                onClick: () => settle(true),
                autoFocus: true,
                children: pending.confirmLabel ?? "Confirm"
              }
            )
          ] })
        ] })
      }
    )
  ] });
}
function useConfirm() {
  const ctx = React5.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within a <ConfirmProvider>");
  }
  return ctx;
}

// src/themePreference.ts
var STORAGE_KEY = "skyxops-theme";
function systemPrefersDark() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}
function safeGetItem(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSetItem(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
}
function safeRemoveItem(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
  }
}
function applyThemePreference(pref) {
  const root = document.documentElement;
  if (pref === "system") {
    safeRemoveItem(STORAGE_KEY);
    root.classList.toggle("dark", systemPrefersDark());
  } else {
    safeSetItem(STORAGE_KEY, pref);
    root.classList.toggle("dark", pref === "dark");
  }
}
function storedThemePreference() {
  const v = safeGetItem(STORAGE_KEY);
  return v === "light" || v === "dark" ? v : "system";
}
function effectiveTheme() {
  const pref = storedThemePreference();
  if (pref === "system") return systemPrefersDark() ? "dark" : "light";
  return pref;
}
async function syncThemeFromProfile(getPref) {
  try {
    applyThemePreference(storedThemePreference());
  } catch {
  }
  try {
    const pref = await getPref();
    if (pref === "light" || pref === "dark" || pref === "system") {
      applyThemePreference(pref);
    }
  } catch {
  }
}
function Switch({
  checked = false,
  onCheckedChange,
  className,
  disabled,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border transition-colors",
        // The track colour is the state. Tokens (not literal colours) so this
        // follows the theme in light and dark without a second definition.
        checked ? "border-primary bg-primary" : "border-input bg-muted",
        disabled && "cursor-not-allowed opacity-50",
        // Focus lives on the parent because the input itself is invisible.
        "has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50",
        className
      ),
      "data-slot": "switch",
      children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "checkbox",
            role: "switch",
            checked,
            disabled,
            onChange: (e) => onCheckedChange?.(e.currentTarget.checked),
            className: "absolute inset-0 z-10 m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed",
            ...props
          }
        ),
        /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            className: cn(
              "pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow-sm transition-transform",
              checked ? "translate-x-[18px]" : "translate-x-0.5"
            )
          }
        )
      ]
    }
  );
}
var STATUS_VARIANTS = {
  // healthy / complete
  active: "success",
  paid: "success",
  enabled: "success",
  approved: "success",
  branding_enabled: "success",
  closed_won: "success",
  onboarding_started: "success",
  succeeded: "success",
  completed: "success",
  // attention / in flight
  pending: "warning",
  sent: "warning",
  overdue: "warning",
  pending_approval: "warning",
  pending_email: "warning",
  pending_review: "warning",
  // informational / running
  provisioning: "info",
  running: "info",
  in_progress: "info",
  // failed / blocked
  failed: "error",
  void: "error",
  suspended: "error",
  error: "error",
  closed_lost: "error",
  disqualified: "error",
  terminated: "error",
  rejected: "error",
  // dormant
  draft: "secondary",
  archived: "secondary",
  disabled: "secondary"
};
function humanize(status) {
  const words = status.replace(/[_-]+/g, " ").trim();
  if (!words) return status;
  return words.charAt(0).toUpperCase() + words.slice(1);
}
function StatusBadge({ status, variant, label, className }) {
  return /* @__PURE__ */ jsx(Badge, { variant: variant ?? STATUS_VARIANTS[status] ?? "secondary", className, children: label ?? humanize(status) });
}
function Breadcrumb({ ...props }) {
  return /* @__PURE__ */ jsx("nav", { "aria-label": "breadcrumb", "data-slot": "breadcrumb", ...props });
}
function BreadcrumbList({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "ol",
    {
      "data-slot": "breadcrumb-list",
      className: cn(
        "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
        className
      ),
      ...props
    }
  );
}
function BreadcrumbItem({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "li",
    {
      "data-slot": "breadcrumb-item",
      className: cn("inline-flex items-center gap-1.5", className),
      ...props
    }
  );
}
function BreadcrumbLink({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot.Root : "a";
  return /* @__PURE__ */ jsx(
    Comp,
    {
      "data-slot": "breadcrumb-link",
      className: cn("transition-colors hover:text-foreground", className),
      ...props
    }
  );
}
function BreadcrumbPage({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      "data-slot": "breadcrumb-page",
      role: "link",
      "aria-disabled": "true",
      "aria-current": "page",
      className: cn("font-medium text-foreground", className),
      ...props
    }
  );
}
function BreadcrumbSeparator({
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "li",
    {
      "data-slot": "breadcrumb-separator",
      role: "presentation",
      "aria-hidden": "true",
      className: cn("text-muted-foreground [&>svg]:size-3.5", className),
      ...props,
      children: children ?? /* @__PURE__ */ jsx(ChevronRight, {})
    }
  );
}
function PageHeader({
  title,
  subtitle,
  actions,
  className
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-start justify-between gap-4", className), children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("h1", { className: "font-display text-[27px] font-bold leading-tight tracking-tight text-foreground", children: title }),
      subtitle && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-muted-foreground", children: subtitle })
    ] }),
    actions && /* @__PURE__ */ jsx("div", { className: "shrink-0 flex items-center gap-2", children: actions })
  ] });
}
function useCommandPalette() {
  const [open, setOpen] = React5.useState(false);
  React5.useEffect(() => {
    function onKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  return { open, setOpen };
}
function matchesQuery(item, q) {
  if (!q) {
    return true;
  }
  const needle = q.toLowerCase();
  return item.label.toLowerCase().includes(needle) || (item.keywords?.toLowerCase().includes(needle) ?? false);
}
function CommandPalette({
  open,
  onOpenChange,
  groups,
  onQueryChange,
  placeholder = "Type a command or search\u2026",
  emptyText = "No results found."
}) {
  const [query, setQuery] = React5.useState("");
  const [activeIndex, setActiveIndex] = React5.useState(0);
  React5.useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);
  const filteredGroups = React5.useMemo(
    () => groups.map((g) => ({
      ...g,
      items: g.items.filter((it) => matchesQuery(it, query))
    })).filter((g) => g.items.length > 0),
    [groups, query]
  );
  const flatItems = React5.useMemo(
    () => filteredGroups.flatMap((g) => g.items),
    [filteredGroups]
  );
  React5.useEffect(() => {
    setActiveIndex(
      (i) => flatItems.length === 0 ? 0 : Math.min(i, flatItems.length - 1)
    );
  }, [flatItems.length]);
  function handleQueryChange(value) {
    setQuery(value);
    setActiveIndex(0);
    onQueryChange?.(value);
  }
  function runItem(item) {
    item.onSelect();
    onOpenChange(false);
  }
  function handleKeyDown(e) {
    if (flatItems.length === 0) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flatItems.length) % flatItems.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flatItems[activeIndex];
      if (item) {
        runItem(item);
      }
    }
  }
  let runningIndex = -1;
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(
    DialogContent,
    {
      showCloseButton: false,
      "aria-describedby": void 0,
      className: "max-w-xl gap-0 overflow-hidden bg-popover p-0 text-popover-foreground",
      children: [
        /* @__PURE__ */ jsx(DialogTitle, { className: "sr-only", children: "Command palette" }),
        /* @__PURE__ */ jsx("div", { className: "border-b p-2", children: /* @__PURE__ */ jsx(
          Input,
          {
            autoFocus: true,
            value: query,
            onChange: (e) => handleQueryChange(e.target.value),
            onKeyDown: handleKeyDown,
            placeholder,
            className: "border-0 shadow-none focus-visible:ring-0"
          }
        ) }),
        /* @__PURE__ */ jsx("div", { className: "max-h-[60vh] overflow-y-auto p-1", children: flatItems.length === 0 ? /* @__PURE__ */ jsx("div", { className: "py-6 text-center text-sm text-muted-foreground", children: emptyText }) : filteredGroups.map((group, gi) => /* @__PURE__ */ jsxs("div", { className: "px-1 py-1", children: [
          group.heading && /* @__PURE__ */ jsx("div", { className: "px-2 py-1.5 text-xs font-medium text-muted-foreground", children: group.heading }),
          group.items.map((item) => {
            runningIndex += 1;
            const isActive = runningIndex === activeIndex;
            const Icon = item.icon;
            return /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                role: "option",
                "aria-selected": isActive,
                onMouseEnter: () => setActiveIndex(flatItems.indexOf(item)),
                onClick: () => runItem(item),
                className: cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none",
                  isActive ? "bg-accent text-accent-foreground" : "text-foreground"
                ),
                children: [
                  Icon && /* @__PURE__ */ jsx(Icon, { className: "size-4 shrink-0 text-muted-foreground" }),
                  /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children: item.label }),
                  item.shortcut && /* @__PURE__ */ jsx("kbd", { className: "ml-auto text-xs tracking-widest text-muted-foreground", children: item.shortcut })
                ]
              },
              item.id
            );
          })
        ] }, group.heading ?? `group-${gi}`)) })
      ]
    }
  ) });
}

// src/csv.ts
var FORMULA_TRIGGER_RE = /^[=+\-@\t\r]/;
function neutralizeFormula(s) {
  if (!FORMULA_TRIGGER_RE.test(s)) return s;
  if (s.trim() !== "" && Number.isFinite(Number(s))) return s;
  return `'${s}`;
}
function escapeCsvField(value) {
  if (value == null) return "";
  const s = neutralizeFormula(String(value));
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
function downloadCsv(filename, columns, rows) {
  const headerLine = columns.map((c) => escapeCsvField(c.header)).join(",");
  const bodyLines = rows.map(
    (row) => columns.map((c) => escapeCsvField(c.value(row))).join(",")
  );
  const csv = [headerLine, ...bodyLines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// src/index.ts
var VERSION = "0.32.0";

export { ALL_FILTER_VALUE, Alert, AlertDescription, AlertTitle, AppShellV2, AreaChart, BackToLauncherButton, Badge, BarChart, Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, BuildStamp, BulkActionBar, Button, CATEGORICAL_PALETTE, CHART_ICON_COLORS, CONFIG_SECTION_TONES, Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, ChartLegend, ChartTooltip, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, CommandPalette, ConfigSection, ConfirmProvider, DEFAULT_DATE_PRESETS, DataTable, DataTableToolbar, DataWindow, DateTime, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, ELEVATED_CARD_CLASSNAME, EmptyState, FilterBar, FilterDateRange, FilterMultiSelect, FilterNumberRange, FilterProvider, FilterSelect, FilterTagInput, FormDialog, FormField, Gallery, GaugeChart, GridFilterSelect, HorizontalBarChart, Input, InspectorPanel, JobStatusBadge, KPI_ACCENTS, KpiCard, KpiGridSkeleton, LineChart, Money, MultiSeriesLineChart, NumberDisplay, PRIMARY_COLOR, PageHeader, Pagination, PieChart, SEMANTIC_COLORS, SankeyChart, SavedFilterChips, SectionCard, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Separator, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger, SideNav, Skeleton, SortableHeader, Spinner, SplitPanel, StackedBarChart, StatusBadge, Switch, Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableLoadingSkeleton, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Toaster, Tooltip9 as Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TopBar, TrendIndicator, VERSION, applyThemePreference, badgeVariants, buttonVariants, chartIconColor, cn, colorFor, downloadCsv, effectiveTheme, formatDateTime, formatMoney, formatNumber, formatTimeBucket, kpiIconFor, paletteFor, storedThemePreference, syncThemeFromProfile, unwrapFilterValue, useChartColors, useCommandPalette, useConfirm, useFilters, useRowSelection, useTableSort };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map